import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { nip19, type Event } from 'nostr-tools';
import {
  Radio, Music, Play, Pause, SkipForward, SkipBack, Volume2, VolumeX,
  Plus, Upload, Loader2, X, Heart, Users, ListMusic, MessageCircle,
  ChevronUp, ChevronDown, Coins, Clock, Disc3, Flag, ShieldCheck,
  AlertTriangle, CheckCircle, Trash2, Shield, Flame, TrendingUp,
  Sparkles, Share2, Repeat2, Send, Music2, Headphones, ToggleLeft,
  ToggleRight, ListPlus, Star, Globe, Mic2, Shuffle, Pencil, MoreVertical,
  Rocket, Sprout, Crown, Megaphone, ArrowRight, Zap, BadgeCheck,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { uploadMedia } from '../lib/imageUpload';
import BCHTipButton from '../components/BCHTipButton';
import { BCHLogo } from '../components/Layout';
import { timeAgo } from '../lib/utils';
import { FLAG_KIND, FLAG_TAG, FLAG_THRESHOLD } from '../lib/communityFlag';
import {
  RADIO_TRACK_KIND,
  RADIO_TRACK_TAG,
  RADIO_PROMO_KIND,
  RADIO_PROMO_TAG,
  PROMO_D_TAG_PREFIX,
  PROMO_TIERS,
  FEATURED_ARTIST_TIERS,
  PROMO_AUCTION_DURATION,
  PROMO_MAX_SONG_SLOTS,
  RADIO_WALLET,
  MAX_QUEUE_SIZE,
  formatDuration,
  isAudioFile,
  satsToUsd,
  usdToSats,
  formatUsd,
  GENRES,
  type RadioTrack,
  type Genre,
} from '../lib/radio';
import { getBCHPrice } from '../hooks/useBCHPrice';
import { useTipWallet } from '../hooks/useTipWallet';
import { OPERATOR_PUBKEY, PLATFORM_FEE_ADDRESS } from '../lib/platform';
import { useRadioPlayer } from '../contexts/RadioPlayerContext';

// ── Tab Types ─────────────────────────────────────────────────────
type MainTab = 'discover' | 'queue' | 'playlists';

// ── Main Radio Page ──────────────────────────────────────────────

export default function RadioPage() {
  const { user, setShowLoginDialog } = useAppContext();
  const radio = useRadioPlayer();
  const [showSubmit, setShowSubmit] = useState(false);
  const [activeTab, setActiveTab] = useState<MainTab>('discover');
  const [selectedTrack, setSelectedTrack] = useState<RadioTrack | null>(null);
  const [editTrack, setEditTrack] = useState<RadioTrack | null>(null);
  const [deleteTrack, setDeleteTrack] = useState<RadioTrack | null>(null);
  const [promoteTrack, setPromoteTrack] = useState<RadioTrack | null>(null);
  const [showFeaturedArtistModal, setShowFeaturedArtistModal] = useState(false);
  const [searchParams] = useSearchParams();

  // Check for ?track= to open a song detail view directly
  useEffect(() => {
    const trackId = searchParams.get('track');
    if (trackId) {
      const track = radio.queue.find(t => t.id === trackId);
      if (track) setSelectedTrack(track);
    }
  }, [searchParams, radio.queue]);

  const queue = radio.queue;
  const currentTrack = radio.currentTrack;
  const currentIndex = radio.currentIndex;
  const isPlaying = radio.isPlaying;
  const loading = radio.loading;
  const currentTime = radio.currentTime;
  const duration = radio.duration;
  const volume = radio.volume;
  const muted = radio.muted;

  const togglePlay = radio.togglePlay;
  const skipNext = radio.skipNext;
  const skipPrev = radio.skipPrev;
  const seekTo = radio.seekTo;
  const playTrack = radio.playTrack;

  // No auto-play — users start playback by clicking a track or the play button

  const listenerCount = useMemo(() => {
    const submitters = new Set(queue.map(t => t.submittedBy)).size;
    return Math.max(submitters, 1);
  }, [queue]);

  // Callbacks for edit/delete from child components
  const handleEditTrack = useCallback((track: RadioTrack) => setEditTrack(track), []);
  const handleDeleteTrack = useCallback((track: RadioTrack) => setDeleteTrack(track), []);

  // Song detail view
  if (selectedTrack) {
    return (
      <>
        <SongDetailView
          track={selectedTrack}
          onBack={() => setSelectedTrack(null)}
          onEdit={handleEditTrack}
          onDelete={handleDeleteTrack}
          onPromote={(t) => setPromoteTrack(t)}
        />
        {/* Modals can appear on top of song detail */}
        {editTrack && (
          <EditTrackModal
            track={editTrack}
            onClose={() => setEditTrack(null)}
            onSaved={() => {
              setEditTrack(null);
              setSelectedTrack(null);
              setTimeout(radio.refetchQueue, 1000);
            }}
          />
        )}
        {deleteTrack && (
          <DeleteTrackModal
            track={deleteTrack}
            onClose={() => setDeleteTrack(null)}
            onDeleted={() => {
              setDeleteTrack(null);
              setSelectedTrack(null);
              setTimeout(radio.refetchQueue, 1000);
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 60px)' }}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Radio className="w-5 h-5 text-primary" />
            BCH Radio
          </h1>
          <div className="flex items-center gap-2">
            {/* Shuffle toggle */}
            <button
              onClick={radio.queue.length > 0 ? radio.shufflePlay : undefined}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                radio.shuffle
                  ? 'bg-primary/10 text-primary border border-primary/30'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              } ${radio.queue.length === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={radio.shuffle ? 'Shuffle is on — click to play a random track' : 'Shuffle Play — play tracks in random order'}
            >
              <Shuffle className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Shuffle</span>
            </button>
            {/* Soundtrack My Feed toggle */}
            <button
              onClick={radio.toggleSoundtrackMode}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                radio.soundtrackMode
                  ? 'bg-primary/10 text-primary border border-primary/30'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
              title="Soundtrack My Feed — music keeps playing while you browse"
            >
              <Music2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Soundtrack</span>
              {radio.soundtrackMode ? (
                <ToggleRight className="w-4 h-4" />
              ) : (
                <ToggleLeft className="w-4 h-4" />
              )}
            </button>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Headphones className="w-3.5 h-3.5" /> {listenerCount}
            </span>
            <button
              onClick={() => user ? setShowSubmit(true) : setShowLoginDialog(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" /> Upload
            </button>
          </div>
        </div>
      </div>

      {/* Content policy bar */}
      <div className="px-4 py-2 bg-muted/30 border-b border-border flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          Only original, Creative Commons, or royalty-free music. <button onClick={() => user ? setShowSubmit(true) : setShowLoginDialog(true)} className="text-primary hover:underline">See guidelines</button>.
          {' '}Use <Flag className="w-2.5 h-2.5 inline" /> to report violations.
        </p>
      </div>

      {/* Hero Banner */}
      <RadioHeroBanner
        queue={queue}
        onBrowse={() => setActiveTab('discover')}
        onUpload={() => user ? setShowSubmit(true) : setShowLoginDialog(true)}
        onFeatureArtist={() => user ? setShowFeaturedArtistModal(true) : setShowLoginDialog(true)}
      />

      {/* Now Playing Hero */}
      <NowPlayingHero
        track={currentTrack}
        isPlaying={isPlaying}
        loading={loading}
        currentTime={currentTime}
        duration={duration}
        volume={volume}
        muted={muted}
        onTogglePlay={togglePlay}
        onSkipNext={skipNext}
        onSkipPrev={skipPrev}
        onSeek={seekTo}
        onVolumeChange={radio.setVolume}
        onToggleMute={radio.toggleMute}
        queueLength={queue.length}
        currentIndex={currentIndex}
        onTrackClick={setSelectedTrack}
      />

      {/* Tab bar */}
      <div className="border-b border-border flex bg-background/90 backdrop-blur-md sticky top-[57px] z-[9]">
        {([
          { id: 'discover' as MainTab, label: 'Discover', icon: <Sparkles className="w-3.5 h-3.5" /> },
          { id: 'queue' as MainTab, label: `Queue (${queue.length})`, icon: <ListMusic className="w-3.5 h-3.5" /> },
          { id: 'playlists' as MainTab, label: 'Playlists', icon: <ListPlus className="w-3.5 h-3.5" /> },
        ]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2.5 text-xs font-bold transition-colors border-b-2 flex items-center justify-center gap-1.5 ${
              activeTab === tab.id ? 'text-primary border-primary' : 'text-muted-foreground border-transparent hover:text-foreground'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1">
        {activeTab === 'discover' && (
          <DiscoverView
            queue={queue}
            currentIndex={currentIndex}
            isPlaying={isPlaying}
            onPlay={playTrack}
            onTrackClick={setSelectedTrack}
            onEditTrack={handleEditTrack}
            onDeleteTrack={handleDeleteTrack}
            onFeatureArtist={() => user ? setShowFeaturedArtistModal(true) : setShowLoginDialog(true)}
          />
        )}

        {activeTab === 'queue' && (
          <QueueView
            queue={queue}
            currentIndex={currentIndex}
            isPlaying={isPlaying}
            onPlay={playTrack}
            onTrackClick={setSelectedTrack}
            onAddTrack={() => user ? setShowSubmit(true) : setShowLoginDialog(true)}
            onEditTrack={handleEditTrack}
            onDeleteTrack={handleDeleteTrack}
          />
        )}

        {activeTab === 'playlists' && (
          <PlaylistsView queue={queue} onTrackClick={setSelectedTrack} />
        )}
      </div>

      {/* Submit Track Modal */}
      {showSubmit && user && (
        <SubmitTrackModal
          onClose={() => setShowSubmit(false)}
          onSubmitted={() => { setShowSubmit(false); setTimeout(radio.refetchQueue, 1000); }}
        />
      )}

      {/* Edit Track Modal */}
      {editTrack && (
        <EditTrackModal
          track={editTrack}
          onClose={() => setEditTrack(null)}
          onSaved={() => {
            setEditTrack(null);
            setTimeout(radio.refetchQueue, 1000);
          }}
        />
      )}

      {/* Delete Track Modal */}
      {deleteTrack && (
        <DeleteTrackModal
          track={deleteTrack}
          onClose={() => setDeleteTrack(null)}
          onDeleted={() => {
            setDeleteTrack(null);
            setTimeout(radio.refetchQueue, 1000);
          }}
        />
      )}

      {/* Promote Song Modal */}
      {promoteTrack && (
        <PromoteSongModal
          track={promoteTrack}
          onClose={() => setPromoteTrack(null)}
          onPromoted={() => {
            setPromoteTrack(null);
            setTimeout(radio.refetchQueue, 1500);
          }}
        />
      )}

      {/* Featured Artist Modal */}
      {showFeaturedArtistModal && user && (
        <FeaturedArtistModal
          onClose={() => setShowFeaturedArtistModal(false)}
          onPromoted={() => {
            setShowFeaturedArtistModal(false);
            setTimeout(radio.refetchQueue, 1500);
          }}
        />
      )}
    </div>
  );
}

// ── Discover View ─────────────────────────────────────────────────

// ── Hero Banner ───────────────────────────────────────────────────

function RadioHeroBanner({
  queue,
  onBrowse,
  onUpload,
  onFeatureArtist,
}: {
  queue: RadioTrack[];
  onBrowse: () => void;
  onUpload: () => void;
  onFeatureArtist: () => void;
}) {
  const radio = useRadioPlayer();

  // Compute live stats
  const totalSongs = queue.length;
  const totalArtists = useMemo(() => new Set(queue.map(t => t.submittedBy)).size, [queue]);
  const totalTipSats = useMemo(() => {
    let total = 0;
    radio.tipCounts.forEach(v => { total += v; });
    return total;
  }, [radio.tipCounts]);
  const tipBCH = totalTipSats / 1e8;

  // Derive "countries" from unique timezones/locale patterns in artist profiles
  // Since we can't know real countries, we estimate from unique artist count
  // A reasonable heuristic: ~1 country per 3-4 unique artists, min 1
  const estimatedCountries = Math.max(1, Math.round(totalArtists / 3));

  // Pick a few random cover arts for the background mosaic
  const coverArts = useMemo(() => {
    const arts = queue.filter(t => t.coverArt).map(t => t.coverArt!);
    // Shuffle and pick up to 6
    const shuffled = [...arts].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 6);
  }, [queue]);

  return (
    <div className="relative overflow-hidden">
      {/* Background — gradient with album art mosaic */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-emerald-500/10 to-primary/5">
        {/* Album art grid overlay */}
        {coverArts.length > 0 && (
          <div className="absolute inset-0 grid grid-cols-3 gap-0.5 opacity-[0.07]">
            {coverArts.map((art, i) => (
              <div key={i} className="w-full h-full">
                <img src={art} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
        {/* Animated music visualization dots */}
        <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center gap-1 pb-2 opacity-20">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="w-1 bg-primary rounded-full animate-pulse"
              style={{
                height: `${12 + Math.random() * 40}px`,
                animationDelay: `${i * 100}ms`,
                animationDuration: `${800 + Math.random() * 600}ms`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative px-4 py-6 sm:py-8">
        <div className="max-w-lg">
          {/* Title */}
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/20 backdrop-blur-sm border border-primary/20 flex items-center justify-center">
              <Radio className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold tracking-tight">BCH Radio</h2>
              <p className="text-xs text-primary font-semibold">The soundtrack to the BCH Nostr community</p>
            </div>
          </div>

          {/* Subtitle */}
          <p className="text-sm text-muted-foreground leading-relaxed mb-5 max-w-md">
            Listen to music from creators around the world while browsing BCH Nostr.
            Discover new artists and support them instantly with Bitcoin Cash.
          </p>

          {/* Stats row */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 mb-5">
            <StatPill icon={<Music className="w-3.5 h-3.5" />} value={totalSongs} label="Songs" />
            <StatPill icon={<Mic2 className="w-3.5 h-3.5" />} value={totalArtists} label="Artists" />
            <StatPill icon={<Globe className="w-3.5 h-3.5" />} value={estimatedCountries} label={estimatedCountries === 1 ? 'Country' : 'Countries'} />
            <StatPill
              icon={<Coins className="w-3.5 h-3.5 text-emerald-500" />}
              value={tipBCH >= 0.01 ? tipBCH.toFixed(2) : totalTipSats > 0 ? `${totalTipSats.toLocaleString()} sats` : '0'}
              label={tipBCH >= 0.01 ? 'BCH Tipped' : totalTipSats > 0 ? 'Tipped' : 'BCH Tipped'}
              highlight
            />
          </div>

          {/* CTA buttons */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              onClick={() => {
                if (totalSongs > 0) {
                  radio.shufflePlay();
                } else {
                  onBrowse();
                }
              }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 active:scale-[0.97] transition-all shadow-lg shadow-primary/20"
            >
              <Shuffle className="w-4 h-4" /> Shuffle Play
            </button>
            <button
              onClick={onBrowse}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border bg-card/80 backdrop-blur-sm text-sm font-bold hover:bg-muted active:scale-[0.97] transition-all"
            >
              <Play className="w-4 h-4" /> Browse Music
            </button>
            <button
              onClick={onUpload}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border bg-card/80 backdrop-blur-sm text-sm font-bold hover:bg-muted active:scale-[0.97] transition-all"
            >
              <Upload className="w-4 h-4" /> Upload Music
            </button>
            <button
              onClick={onFeatureArtist}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 backdrop-blur-sm text-sm font-bold text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 active:scale-[0.97] transition-all"
            >
              <Crown className="w-4 h-4" /> Bid for Featured
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatPill({ icon, value, label, highlight }: {
  icon: React.ReactNode;
  value: number | string;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg backdrop-blur-sm border ${
      highlight
        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400'
        : 'bg-card/60 border-border/60 text-foreground'
    }`}>
      {icon}
      <span className="text-sm font-bold tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</span>
      <span className={`text-[10px] ${highlight ? 'text-emerald-600/70 dark:text-emerald-400/70' : 'text-muted-foreground'}`}>{label}</span>
    </div>
  );
}

// ── Discover View ─────────────────────────────────────────────────

function DiscoverView({
  queue,
  currentIndex,
  isPlaying,
  onPlay,
  onTrackClick,
  onEditTrack,
  onDeleteTrack,
  onFeatureArtist,
}: {
  queue: RadioTrack[];
  currentIndex: number;
  isPlaying: boolean;
  onPlay: (i: number) => void;
  onTrackClick: (track: RadioTrack) => void;
  onEditTrack?: (track: RadioTrack) => void;
  onDeleteTrack?: (track: RadioTrack) => void;
  onFeatureArtist?: () => void;
}) {
  const radio = useRadioPlayer();
  const [genreFilter, setGenreFilter] = useState<Genre>('All');

  // Compute discovery sections
  const now = Math.floor(Date.now() / 1000);

  const newReleases = useMemo(() =>
    [...queue].sort((a, b) => b.submittedAt - a.submittedAt).slice(0, 10),
    [queue]
  );

  const trending = useMemo(() => {
    const weekAgo = now - 86400 * 7;
    const recent = queue.filter(t => t.submittedAt > weekAgo);
    return recent.sort((a, b) => {
      const aScore = (radio.likeCounts.get(a.id) || 0) * 3 + (radio.commentCounts.get(a.id) || 0) * 2 + (radio.tipCounts.get(a.id) || 0);
      const bScore = (radio.likeCounts.get(b.id) || 0) * 3 + (radio.commentCounts.get(b.id) || 0) * 2 + (radio.tipCounts.get(b.id) || 0);
      return bScore - aScore;
    }).slice(0, 10);
  }, [queue, radio.likeCounts, radio.commentCounts, radio.tipCounts, now]);

  const mostTipped = useMemo(() =>
    [...queue].sort((a, b) => (radio.tipCounts.get(b.id) || 0) - (radio.tipCounts.get(a.id) || 0))
      .filter(t => (radio.tipCounts.get(t.id) || 0) > 0)
      .slice(0, 10),
    [queue, radio.tipCounts]
  );

  const mostLiked = useMemo(() =>
    [...queue].sort((a, b) => (radio.likeCounts.get(b.id) || 0) - (radio.likeCounts.get(a.id) || 0))
      .filter(t => (radio.likeCounts.get(t.id) || 0) > 0)
      .slice(0, 10),
    [queue, radio.likeCounts]
  );

  const recentlyPlayed = useMemo(() => {
    return radio.playHistory
      .map(id => queue.find(t => t.id === id))
      .filter((t): t is RadioTrack => !!t)
      .slice(0, 10);
  }, [radio.playHistory, queue]);

  // Genre-filtered full list
  const genreFiltered = useMemo(() => {
    if (genreFilter === 'All') return [...queue].sort((a, b) => b.submittedAt - a.submittedAt);
    return queue
      .filter(t => t.genre?.toLowerCase() === genreFilter.toLowerCase())
      .sort((a, b) => b.submittedAt - a.submittedAt);
  }, [queue, genreFilter]);

  return (
    <div className="pb-8">
      {/* Genre pills */}
      <div className="px-4 py-3 overflow-x-auto scrollbar-none">
        <div className="flex gap-1.5">
          {GENRES.map(g => (
            <button
              key={g}
              onClick={() => setGenreFilter(g)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                genreFilter === g
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {genreFilter !== 'All' ? (
        // Show genre-filtered list
        <div>
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4" />
              <h2 className="text-sm font-bold">{genreFilter}</h2>
              <span className="text-xs text-muted-foreground">({genreFiltered.length})</span>
            </div>
            {genreFiltered.length > 1 && (
              <button
                onClick={() => {
                  // Enable shuffle and play a random track from this genre
                  const randomTrack = genreFiltered[Math.floor(Math.random() * genreFiltered.length)];
                  const qIdx = queue.findIndex(t => t.id === randomTrack.id);
                  if (qIdx >= 0) {
                    radio.shufflePlay();
                  }
                }}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary/10 text-primary text-[11px] font-medium hover:bg-primary/20 transition-colors"
              >
                <Shuffle className="w-3 h-3" /> Shuffle
              </button>
            )}
          </div>
          {genreFiltered.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Music className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No tracks in this genre yet</p>
            </div>
          ) : (
            genreFiltered.map((track, i) => {
              const qIdx = queue.findIndex(t => t.id === track.id);
              return (
                <TrackRow
                  key={track.id}
                  track={track}
                  index={qIdx}
                  isActive={qIdx === currentIndex}
                  isPlaying={qIdx === currentIndex && isPlaying}
                  onPlay={() => qIdx >= 0 && onPlay(qIdx)}
                  onTrackClick={() => onTrackClick(track)}
                  onEdit={onEditTrack}
                  onDelete={onDeleteTrack}
                />
              );
            })
          )}
        </div>
      ) : (
        <>
          {/* Shuffle All quick action */}
          {queue.length > 1 && (
            <div className="px-4 pt-3 pb-1">
              <button
                onClick={() => radio.shufflePlay()}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all active:scale-[0.98] ${
                  radio.shuffle
                    ? 'bg-primary/10 text-primary border border-primary/30 shadow-sm'
                    : 'bg-gradient-to-r from-primary/5 to-primary/10 border border-border hover:border-primary/30 text-foreground hover:text-primary'
                }`}
              >
                <Shuffle className="w-4 h-4" />
                {radio.shuffle ? 'Shuffle is On — Tap to Play Random' : 'Shuffle All Tracks'}
                {radio.shuffle && (
                  <button
                    onClick={(e) => { e.stopPropagation(); radio.toggleShuffle(); }}
                    className="ml-2 px-2 py-0.5 rounded-md bg-primary/20 text-primary text-[10px] font-semibold hover:bg-primary/30 transition-colors"
                  >
                    Turn Off
                  </button>
                )}
              </button>
            </div>
          )}

          {/* Featured Artist Banner */}
          <FeaturedArtistBanner queue={queue} onTrackClick={onTrackClick} onFeatureArtist={onFeatureArtist} />

          {/* Promoted / Featured Songs */}
          <PromotedSongsSection queue={queue} currentIndex={currentIndex} isPlaying={isPlaying} onPlay={onPlay} onTrackClick={onTrackClick} />

          {/* Emerging Artists */}
          <EmergingArtistsSection queue={queue} currentIndex={currentIndex} isPlaying={isPlaying} onPlay={onPlay} onTrackClick={onTrackClick} />

          {/* Trending */}
          {trending.length > 0 && (
            <TrackSection
              icon={<Flame className="w-4 h-4 text-orange-500" />}
              title="Trending"
              tracks={trending}
              queue={queue}
              currentIndex={currentIndex}
              isPlaying={isPlaying}
              onPlay={onPlay}
              onTrackClick={onTrackClick}
            />
          )}

          {/* New Releases */}
          {newReleases.length > 0 && (
            <TrackSection
              icon={<Sparkles className="w-4 h-4 text-purple-500" />}
              title="New Releases"
              tracks={newReleases}
              queue={queue}
              currentIndex={currentIndex}
              isPlaying={isPlaying}
              onPlay={onPlay}
              onTrackClick={onTrackClick}
            />
          )}

          {/* Most Tipped */}
          {mostTipped.length > 0 && (
            <TrackSection
              icon={<Coins className="w-4 h-4 text-emerald-500" />}
              title="Most Tipped"
              tracks={mostTipped}
              queue={queue}
              currentIndex={currentIndex}
              isPlaying={isPlaying}
              onPlay={onPlay}
              onTrackClick={onTrackClick}
              showTips
            />
          )}

          {/* Most Liked */}
          {mostLiked.length > 0 && (
            <TrackSection
              icon={<Heart className="w-4 h-4 text-red-500" />}
              title="Most Liked"
              tracks={mostLiked}
              queue={queue}
              currentIndex={currentIndex}
              isPlaying={isPlaying}
              onPlay={onPlay}
              onTrackClick={onTrackClick}
              showLikes
            />
          )}

          {/* Recently Played */}
          {recentlyPlayed.length > 0 && (
            <TrackSection
              icon={<Clock className="w-4 h-4 text-sky-500" />}
              title="Recently Played"
              tracks={recentlyPlayed}
              queue={queue}
              currentIndex={currentIndex}
              isPlaying={isPlaying}
              onPlay={onPlay}
              onTrackClick={onTrackClick}
            />
          )}

          {/* All tracks (if nothing above or as a fallback) */}
          {queue.length === 0 && (
            <div className="text-center py-16 px-4">
              <Music className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
              <p className="text-sm font-medium">No music yet</p>
              <p className="text-xs text-muted-foreground mt-1">Be the first artist to upload a track!</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Helpers: Display bid amounts in USD ──────────────────────────────

function FeaturedBidDisplay({ paidSats }: { paidSats: number }) {
  const [bchPrice] = useState(() => getBCHPrice() || 0);
  const usd = bchPrice > 0 ? satsToUsd(paidSats, bchPrice) : 0;
  return (
    <span className="text-[9px] text-muted-foreground flex items-center gap-0.5">
      <Coins className="w-2.5 h-2.5" />{usd > 0 ? formatUsd(usd) + ' bid' : paidSats.toLocaleString() + ' sats'}
    </span>
  );
}

function PromoBidLabel({ paidSats }: { paidSats: number }) {
  const [bchPrice] = useState(() => getBCHPrice() || 0);
  const usd = bchPrice > 0 ? satsToUsd(paidSats, bchPrice) : 0;
  return (
    <p className="text-[9px] text-amber-600/70 dark:text-amber-400/70 mt-0.5 flex items-center gap-0.5">
      <Coins className="w-2.5 h-2.5" />{usd > 0 ? formatUsd(usd) + ' bid' : paidSats.toLocaleString() + ' sats'}
    </p>
  );
}

// ── Featured Artist Banner ─────────────────────────────────────────

function FeaturedArtistBanner({ queue, onTrackClick, onFeatureArtist }: {
  queue: RadioTrack[];
  onTrackClick: (track: RadioTrack) => void;
  onFeatureArtist?: () => void;
}) {
  const radio = useRadioPlayer();
  const { user } = useAppContext();
  const featured = radio.featuredArtist;
  const auction = radio.featuredArtistAuction;

  // If no featured artist, show an auction CTA
  if (!featured) {
    return (
      <div className="mx-4 mt-3 mb-1">
        <button
          onClick={onFeatureArtist}
          className="w-full flex items-center gap-3 p-3 rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 hover:border-amber-500/50 transition-all text-left"
        >
          <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
            <Crown className="w-5 h-5 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-amber-600 dark:text-amber-400">Featured Artist Slot Open!</p>
            <p className="text-[10px] text-muted-foreground">
              Place the first bid to claim the premium banner spot. Starting at just $0.10!
            </p>
          </div>
          <div className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500 text-white text-[10px] font-bold shrink-0">
            <Zap className="w-3 h-3" /> Bid Now
          </div>
        </button>
      </div>
    );
  }

  const { profile } = useNostrProfile(featured.pubkey);
  const artistTracks = useMemo(() => queue.filter(t => t.submittedBy === featured.pubkey), [queue, featured.pubkey]);

  if (artistTracks.length === 0) return null;

  const timeLeft = featured.expiresAt - Math.floor(Date.now() / 1000);
  const hoursLeft = Math.max(0, Math.floor(timeLeft / 3600));
  const displayName = profile?.display_name || profile?.name || featured.pubkey.slice(0, 8);

  return (
    <div className="mx-4 mt-3 mb-1 rounded-xl overflow-hidden border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-primary/5">
      <div className="p-4 flex items-center gap-3">
        {/* Artist avatar */}
        <Link to={'/profile/' + featured.pubkey} className="shrink-0">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-amber-500/40" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center border-2 border-amber-500/40">
              <Crown className="w-6 h-6 text-amber-500" />
            </div>
          )}
        </Link>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Crown className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wide">Featured Artist</span>
          </div>
          <Link to={'/profile/' + featured.pubkey} className="text-sm font-bold hover:text-primary transition-colors truncate block">{displayName}</Link>
          <div className="flex items-center gap-2 mt-0.5">
            <FeaturedBidDisplay paidSats={featured.paidSats} />
            <span className="text-[9px] text-muted-foreground flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{hoursLeft}h left</span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button
            onClick={() => {
              if (artistTracks.length > 0) {
                const idx = queue.findIndex(t => t.id === artistTracks[0].id);
                if (idx >= 0) radio.playTrack(idx);
              }
            }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-[10px] font-bold hover:bg-amber-600 transition-colors"
          >
            <Play className="w-3 h-3" /> Listen
          </button>
          <button
            onClick={onFeatureArtist}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-amber-500/30 text-amber-600 dark:text-amber-400 text-[9px] font-bold hover:bg-amber-500/10 transition-colors"
          >
            <Zap className="w-2.5 h-2.5" /> Outbid
          </button>
        </div>
      </div>

      {/* Mini track list */}
      {artistTracks.length > 0 && (
        <div className="border-t border-amber-500/15 px-4 py-2 flex gap-2 overflow-x-auto scrollbar-none">
          {artistTracks.slice(0, 5).map(track => {
            const qIdx = queue.findIndex(t => t.id === track.id);
            const isActive = qIdx === radio.currentIndex;
            return (
              <button
                key={track.id}
                onClick={() => onTrackClick(track)}
                className={'shrink-0 flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition-colors ' + (isActive ? 'bg-amber-500/15' : 'hover:bg-amber-500/10')}
              >
                <div className="w-7 h-7 rounded bg-muted flex items-center justify-center overflow-hidden shrink-0">
                  {track.coverArt ? <img src={track.coverArt} alt="" className="w-full h-full object-cover" /> : <Music className="w-3 h-3 text-muted-foreground/40" />}
                </div>
                <div className="min-w-0">
                  <p className={'text-[10px] font-medium truncate max-w-[100px] ' + (isActive ? 'text-amber-600 dark:text-amber-400' : '')}>{track.title}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Promoted Songs Section (Auction) ───────────────────────────────

function PromotedSongsSection({ queue, currentIndex, isPlaying, onPlay, onTrackClick }: {
  queue: RadioTrack[];
  currentIndex: number;
  isPlaying: boolean;
  onPlay: (i: number) => void;
  onTrackClick: (track: RadioTrack) => void;
}) {
  const radio = useRadioPlayer();
  const auction = radio.songPromoAuction;

  // Map trackId → promo for bid display
  const promoByTrack = useMemo(() => {
    const m = new Map<string, typeof radio.promotedTracks[0]>();
    for (const p of radio.promotedTracks) m.set(p.trackId, p);
    return m;
  }, [radio.promotedTracks]);

  // Get promoted tracks that are still in the queue (top N by bid)
  const promotedSongs = useMemo(() => {
    const topTrackIds = new Set(auction.bids.slice(0, PROMO_MAX_SONG_SLOTS).map(b => b.trackId));
    return queue.filter(t => topTrackIds.has(t.id));
  }, [auction.bids, queue]);

  if (promotedSongs.length === 0) return null;

  return (
    <div className="mb-2">
      <div className="flex items-center gap-2 px-4 pt-4 pb-1">
        <Megaphone className="w-4 h-4 text-amber-500" />
        <h2 className="text-sm font-bold">Featured Songs</h2>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 font-bold uppercase">Auction</span>
        <span className="text-[9px] text-muted-foreground ml-auto">{auction.slotsUsed}/{PROMO_MAX_SONG_SLOTS} slots</span>
      </div>
      <div className="overflow-x-auto scrollbar-none px-4 pb-2">
        <div className="flex gap-3" style={{ minWidth: 'min-content' }}>
          {promotedSongs.map(track => {
            const qIdx = queue.findIndex(t => t.id === track.id);
            const active = qIdx === currentIndex;
            const playing = active && isPlaying;
            const promo = promoByTrack.get(track.id);
            return (
              <button
                key={track.id}
                onClick={() => onTrackClick(track)}
                className={'shrink-0 w-36 rounded-xl border overflow-hidden text-left transition-all hover:shadow-md ' + (
                  active ? 'border-amber-500 bg-amber-500/5' : 'border-amber-500/20 bg-card hover:border-amber-500/40'
                )}
              >
                <div className="w-36 h-36 relative bg-muted flex items-center justify-center">
                  {track.coverArt ? (
                    <img src={track.coverArt} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Disc3 className={'w-12 h-12 text-muted-foreground/20 ' + (playing ? 'animate-spin' : '')} style={playing ? { animationDuration: '3s' } : undefined} />
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); if (qIdx >= 0) onPlay(qIdx); }}
                    className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                      {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                    </div>
                  </button>
                  {/* Promoted badge with bid amount in cents */}
                  <div className="absolute top-1.5 left-1.5 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/90 text-white">
                    <Rocket className="w-2.5 h-2.5" />
                    <span className="text-[8px] font-bold">PROMOTED</span>
                  </div>
                </div>
                <div className="p-2.5">
                  <p className={'text-xs font-semibold truncate ' + (active ? 'text-amber-600 dark:text-amber-400' : '')}>{track.title}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{track.artist}</p>
                  {promo && (
                    <PromoBidLabel paidSats={promo.paidSats} />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Emerging Artists Section ───────────────────────────────────────

function EmergingArtistsSection({ queue, currentIndex, isPlaying, onPlay, onTrackClick }: {
  queue: RadioTrack[];
  currentIndex: number;
  isPlaying: boolean;
  onPlay: (i: number) => void;
  onTrackClick: (track: RadioTrack) => void;
}) {
  const radio = useRadioPlayer();

  // Find artists with low engagement (< 3 total likes+tips across all their tracks)
  // and who uploaded within the last 30 days. Rotate the selection daily.
  const emergingTracks = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const thirtyDaysAgo = now - 86400 * 30;

    // Group tracks by artist pubkey
    const byArtist = new Map<string, RadioTrack[]>();
    for (const track of queue) {
      if (track.submittedAt < thirtyDaysAgo) continue;
      if (!byArtist.has(track.submittedBy)) byArtist.set(track.submittedBy, []);
      byArtist.get(track.submittedBy)!.push(track);
    }

    // Filter to artists with low engagement
    const emerging: { pubkey: string; tracks: RadioTrack[]; totalScore: number }[] = [];
    byArtist.forEach((tracks, pubkey) => {
      let totalScore = 0;
      for (const t of tracks) {
        totalScore += (radio.likeCounts.get(t.id) || 0) + (radio.tipCounts.get(t.id) || 0 > 0 ? 1 : 0);
      }
      // Low engagement: fewer than 5 total likes/tips combined
      if (totalScore < 5) {
        emerging.push({ pubkey, tracks, totalScore });
      }
    });

    if (emerging.length === 0) return [];

    // Daily rotation: use the day-of-year as a seed to pick a different set each day
    const dayOfYear = Math.floor(now / 86400);
    const shuffled = [...emerging].sort((a, b) => {
      const hashA = (a.pubkey.charCodeAt(0) + dayOfYear * 7) % 1000;
      const hashB = (b.pubkey.charCodeAt(0) + dayOfYear * 7) % 1000;
      return hashA - hashB;
    });

    // Pick up to 4 emerging artists, take their first track each + extras
    const result: RadioTrack[] = [];
    for (const artist of shuffled.slice(0, 4)) {
      // Sort their tracks by newest first and add up to 2 per artist
      const sorted = [...artist.tracks].sort((a, b) => b.submittedAt - a.submittedAt);
      result.push(...sorted.slice(0, 2));
    }
    return result.slice(0, 8);
  }, [queue, radio.likeCounts, radio.tipCounts]);

  if (emergingTracks.length === 0) return null;

  return (
    <div className="mb-2">
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        <Sprout className="w-4 h-4 text-green-500" />
        <h2 className="text-sm font-bold">Emerging Artists</h2>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 font-bold">NEW</span>
      </div>
      <p className="px-4 -mt-1 mb-2 text-[10px] text-muted-foreground">Discover new creators — this section rotates daily</p>
      <div className="overflow-x-auto scrollbar-none px-4 pb-2">
        <div className="flex gap-3" style={{ minWidth: 'min-content' }}>
          {emergingTracks.map(track => {
            const qIdx = queue.findIndex(t => t.id === track.id);
            const active = qIdx === currentIndex;
            const playing = active && isPlaying;
            return (
              <button
                key={track.id}
                onClick={() => onTrackClick(track)}
                className={'shrink-0 w-36 rounded-xl border overflow-hidden text-left transition-all hover:shadow-md ' + (
                  active ? 'border-green-500 bg-green-500/5' : 'border-border bg-card hover:border-green-500/30'
                )}
              >
                <div className="w-36 h-36 relative bg-muted flex items-center justify-center">
                  {track.coverArt ? (
                    <img src={track.coverArt} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Disc3 className={'w-12 h-12 text-muted-foreground/20 ' + (playing ? 'animate-spin' : '')} style={playing ? { animationDuration: '3s' } : undefined} />
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); if (qIdx >= 0) onPlay(qIdx); }}
                    className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                      {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                    </div>
                  </button>
                  <div className="absolute top-1.5 left-1.5 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-green-500/90 text-white">
                    <Sprout className="w-2.5 h-2.5" />
                    <span className="text-[8px] font-bold">NEW ARTIST</span>
                  </div>
                </div>
                <div className="p-2.5">
                  <p className={'text-xs font-semibold truncate ' + (active ? 'text-green-600 dark:text-green-400' : '')}>{track.title}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{track.artist}</p>
                  {track.genre && <p className="text-[9px] text-muted-foreground/70 mt-0.5">{track.genre}</p>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Track Section (horizontal scrollable cards) ───────────────────

function SectionHeader({ icon, title, count }: { icon: React.ReactNode; title: string; count?: number }) {
  return (
    <div className="flex items-center gap-2 px-4 pt-4 pb-2">
      {icon}
      <h2 className="text-sm font-bold">{title}</h2>
      {count !== undefined && <span className="text-xs text-muted-foreground">({count})</span>}
    </div>
  );
}

function TrackSection({
  icon,
  title,
  tracks,
  queue,
  currentIndex,
  isPlaying,
  onPlay,
  onTrackClick,
  showTips,
  showLikes,
}: {
  icon: React.ReactNode;
  title: string;
  tracks: RadioTrack[];
  queue: RadioTrack[];
  currentIndex: number;
  isPlaying: boolean;
  onPlay: (i: number) => void;
  onTrackClick: (track: RadioTrack) => void;
  showTips?: boolean;
  showLikes?: boolean;
}) {
  const radio = useRadioPlayer();

  return (
    <div className="mb-2">
      <SectionHeader icon={icon} title={title} count={tracks.length} />
      <div className="overflow-x-auto scrollbar-none px-4 pb-2">
        <div className="flex gap-3" style={{ minWidth: 'min-content' }}>
          {tracks.map(track => {
            const qIdx = queue.findIndex(t => t.id === track.id);
            const active = qIdx === currentIndex;
            const playing = active && isPlaying;
            return (
              <button
                key={track.id}
                onClick={() => onTrackClick(track)}
                className={`shrink-0 w-36 rounded-xl border overflow-hidden text-left transition-all hover:shadow-md ${
                  active ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/30'
                }`}
              >
                {/* Cover art */}
                <div className="w-36 h-36 relative bg-muted flex items-center justify-center">
                  {track.coverArt ? (
                    <img src={track.coverArt} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Disc3 className={`w-12 h-12 text-muted-foreground/20 ${playing ? 'animate-spin' : ''}`} style={playing ? { animationDuration: '3s' } : undefined} />
                  )}
                  {/* Play overlay */}
                  <button
                    onClick={(e) => { e.stopPropagation(); if (qIdx >= 0) onPlay(qIdx); }}
                    className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                      {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                    </div>
                  </button>
                  {playing && (
                    <div className="absolute bottom-1 right-1 flex items-end gap-0.5 h-3">
                      <div className="w-0.5 bg-primary rounded-full animate-pulse" style={{ height: '60%' }} />
                      <div className="w-0.5 bg-primary rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                      <div className="w-0.5 bg-primary rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="p-2.5">
                  <p className={`text-xs font-semibold truncate ${active ? 'text-primary' : ''}`}>{track.title}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{track.artist}</p>
                  {track.genre && <p className="text-[9px] text-muted-foreground/70 mt-0.5">{track.genre}</p>}
                  <div className="flex items-center gap-2 mt-1">
                    {showLikes && (
                      <span className="flex items-center gap-0.5 text-[10px] text-red-500">
                        <Heart className="w-2.5 h-2.5" /> {radio.likeCounts.get(track.id) || 0}
                      </span>
                    )}
                    {showTips && (radio.tipCounts.get(track.id) || 0) > 0 && (
                      <span className="flex items-center gap-0.5 text-[10px] text-emerald-500 font-medium" title={`${(radio.tipCounts.get(track.id) || 0).toLocaleString()} sats earned`}>
                        <Coins className="w-2.5 h-2.5" /> {formatTipAmount(radio.tipCounts.get(track.id) || 0)}
                      </span>
                    )}
                    {!showLikes && !showTips && (radio.tipCounts.get(track.id) || 0) > 0 && (
                      <span className="flex items-center gap-0.5 text-[10px] text-emerald-500 font-medium" title={`${(radio.tipCounts.get(track.id) || 0).toLocaleString()} sats earned`}>
                        <Coins className="w-2.5 h-2.5" /> {formatTipAmount(radio.tipCounts.get(track.id) || 0)}
                      </span>
                    )}
                    {!showLikes && !showTips && (radio.tipCounts.get(track.id) || 0) === 0 && (
                      <span className="text-[10px] text-muted-foreground">{timeAgo(track.submittedAt)}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Queue View ────────────────────────────────────────────────────

function QueueView({
  queue,
  currentIndex,
  isPlaying,
  onPlay,
  onTrackClick,
  onAddTrack,
  onEditTrack,
  onDeleteTrack,
}: {
  queue: RadioTrack[];
  currentIndex: number;
  isPlaying: boolean;
  onPlay: (i: number) => void;
  onTrackClick: (track: RadioTrack) => void;
  onAddTrack: () => void;
  onEditTrack?: (track: RadioTrack) => void;
  onDeleteTrack?: (track: RadioTrack) => void;
}) {
  return (
    <div>
      {queue.length === 0 ? (
        <div className="text-center py-16 px-4">
          <Music className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium">The queue is empty</p>
          <p className="text-xs text-muted-foreground mt-1">Be the first to add a track!</p>
          <button
            onClick={onAddTrack}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add a Track
          </button>
        </div>
      ) : (
        <div>
          {queue.map((track, i) => (
            <TrackRow
              key={track.id}
              track={track}
              index={i}
              isActive={i === currentIndex}
              isPlaying={i === currentIndex && isPlaying}
              onPlay={() => onPlay(i)}
              onTrackClick={() => onTrackClick(track)}
              onEdit={onEditTrack}
              onDelete={onDeleteTrack}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Playlists View ────────────────────────────────────────────────

function PlaylistsView({ queue, onTrackClick }: { queue: RadioTrack[]; onTrackClick: (track: RadioTrack) => void }) {
  // Pre-built community playlists based on genre
  const playlists = useMemo(() => {
    const byGenre = new Map<string, RadioTrack[]>();
    for (const track of queue) {
      const genre = track.genre || 'Other';
      if (!byGenre.has(genre)) byGenre.set(genre, []);
      byGenre.get(genre)!.push(track);
    }
    return Array.from(byGenre.entries())
      .filter(([_, tracks]) => tracks.length >= 1)
      .sort((a, b) => b[1].length - a[1].length);
  }, [queue]);

  const radio = useRadioPlayer();

  const presetPlaylists = useMemo(() => [
    { name: 'BCH Chill', emoji: '\u{1F30A}', tracks: queue.filter(t => t.genre === 'Lo-Fi' || t.genre === 'Ambient') },
    { name: 'Bitcoin Cash Beats', emoji: '\u{1F3A7}', tracks: queue.filter(t => t.genre === 'Hip Hop' || t.genre === 'Electronic') },
    { name: 'Community Favorites', emoji: '\u{2B50}', tracks: [...queue].sort((a, b) => {
      const aScore = (radio.likeCounts.get(a.id) || 0) * 3 + (radio.tipCounts.get(a.id) || 0);
      const bScore = (radio.likeCounts.get(b.id) || 0) * 3 + (radio.tipCounts.get(b.id) || 0);
      return bScore - aScore;
    }).slice(0, 20) },
    { name: 'Coding Music', emoji: '\u{1F4BB}', tracks: queue.filter(t => t.genre === 'Lo-Fi' || t.genre === 'Electronic' || t.genre === 'Ambient') },
  ].filter(p => p.tracks.length > 0), [queue, radio.likeCounts, radio.tipCounts]);

  // Track which playlists are expanded
  const [expandedPreset, setExpandedPreset] = useState<string | null>(null);
  const [expandedGenre, setExpandedGenre] = useState<string | null>(null);

  return (
    <div className="pb-8">
      {/* Preset playlists */}
      {presetPlaylists.length > 0 && (
        <div className="px-4 pt-4 pb-2">
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" /> Community Playlists
          </h3>
          <div className="space-y-3">
            {presetPlaylists.map(pl => {
              const isExpanded = expandedPreset === pl.name;
              return (
                <div
                  key={pl.name}
                  className="rounded-xl border border-border bg-card overflow-hidden hover:border-primary/30 hover:shadow-sm transition-all"
                >
                  {/* Playlist header */}
                  <div className="p-3 flex items-center gap-3">
                    <div className="text-2xl shrink-0">{pl.emoji}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate">{pl.name}</p>
                      <p className="text-[10px] text-muted-foreground">{pl.tracks.length} tracks</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => {
                          if (pl.tracks.length > 0) {
                            const firstTrack = pl.tracks[0];
                            const idx = queue.findIndex(t => t.id === firstTrack.id);
                            if (idx >= 0) radio.playTrack(idx);
                          }
                        }}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg bg-primary text-primary-foreground text-[10px] font-medium hover:bg-primary/90 transition-colors"
                      >
                        <Play className="w-2.5 h-2.5" /> Play
                      </button>
                      {pl.tracks.length > 1 && (
                        <button
                          onClick={() => radio.shufflePlay()}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/10 text-primary text-[10px] font-medium hover:bg-primary/20 transition-colors"
                        >
                          <Shuffle className="w-2.5 h-2.5" /> Shuffle
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Song list toggle */}
                  <button
                    onClick={() => setExpandedPreset(isExpanded ? null : pl.name)}
                    className="w-full flex items-center justify-center gap-1 py-1.5 border-t border-border text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                  >
                    {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {isExpanded ? 'Hide songs' : 'Show songs'}
                  </button>

                  {/* Expanded song list */}
                  {isExpanded && (
                    <div className="border-t border-border">
                      {pl.tracks.map((track, i) => {
                        const qIdx = queue.findIndex(t => t.id === track.id);
                        const isActive = qIdx === radio.currentIndex;
                        const isThisPlaying = isActive && radio.isPlaying;
                        return (
                          <button
                            key={track.id}
                            onClick={() => onTrackClick(track)}
                            className={'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted/30 ' + (isActive ? 'bg-primary/5' : '')}
                          >
                            {/* Play/number indicator */}
                            <div className="w-5 text-center shrink-0">
                              {isThisPlaying ? (
                                <div className="flex items-end justify-center gap-px h-3">
                                  <div className="w-0.5 bg-primary rounded-full animate-pulse" style={{ height: '60%' }} />
                                  <div className="w-0.5 bg-primary rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                                  <div className="w-0.5 bg-primary rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
                                </div>
                              ) : (
                                <span className="text-[10px] text-muted-foreground font-mono">{i + 1}</span>
                              )}
                            </div>
                            {/* Cover art */}
                            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                              {track.coverArt ? (
                                <img src={track.coverArt} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Music className="w-3 h-3 text-muted-foreground/40" />
                              )}
                            </div>
                            {/* Track info */}
                            <div className="flex-1 min-w-0">
                              <p className={'text-xs font-medium truncate ' + (isActive ? 'text-primary' : '')}>{track.title}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{track.artist}</p>
                            </div>
                            {/* Duration */}
                            {track.duration > 0 && (
                              <span className="text-[10px] text-muted-foreground font-mono shrink-0">{formatDuration(track.duration)}</span>
                            )}
                            {/* Play button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (qIdx >= 0) radio.playTrack(qIdx);
                              }}
                              className="p-1 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors shrink-0"
                            >
                              {isThisPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                            </button>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Genre playlists */}
      {playlists.length > 0 && (
        <div className="px-4 pt-4">
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <Music className="w-4 h-4 text-primary" /> By Genre
          </h3>
          <div className="space-y-2">
            {playlists.map(([genre, tracks]) => {
              const isExpanded = expandedGenre === genre;
              return (
                <div
                  key={genre}
                  className="rounded-xl border border-border bg-card overflow-hidden hover:border-primary/30 transition-all"
                >
                  {/* Genre header */}
                  <div className="flex items-center gap-3 p-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Music className="w-5 h-5 text-primary" />
                    </div>
                    <button
                      onClick={() => setExpandedGenre(isExpanded ? null : genre)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <p className="text-sm font-semibold">{genre}</p>
                      <p className="text-xs text-muted-foreground">{tracks.length} track{tracks.length !== 1 ? 's' : ''}</p>
                    </button>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {tracks.length > 1 && (
                        <button
                          onClick={() => radio.shufflePlay()}
                          className="p-2 rounded-lg text-primary hover:bg-primary/10 transition-colors"
                          title="Shuffle play"
                        >
                          <Shuffle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          const idx = queue.findIndex(t => t.id === tracks[0].id);
                          if (idx >= 0) radio.playTrack(idx);
                        }}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        title="Play from start"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setExpandedGenre(isExpanded ? null : genre)}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded song list */}
                  {isExpanded && (
                    <div className="border-t border-border">
                      {tracks.map((track, i) => {
                        const qIdx = queue.findIndex(t => t.id === track.id);
                        const isActive = qIdx === radio.currentIndex;
                        const isThisPlaying = isActive && radio.isPlaying;
                        return (
                          <button
                            key={track.id}
                            onClick={() => onTrackClick(track)}
                            className={'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted/30 ' + (isActive ? 'bg-primary/5' : '')}
                          >
                            <div className="w-5 text-center shrink-0">
                              {isThisPlaying ? (
                                <div className="flex items-end justify-center gap-px h-3">
                                  <div className="w-0.5 bg-primary rounded-full animate-pulse" style={{ height: '60%' }} />
                                  <div className="w-0.5 bg-primary rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                                  <div className="w-0.5 bg-primary rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
                                </div>
                              ) : (
                                <span className="text-[10px] text-muted-foreground font-mono">{i + 1}</span>
                              )}
                            </div>
                            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                              {track.coverArt ? (
                                <img src={track.coverArt} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Music className="w-3 h-3 text-muted-foreground/40" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={'text-xs font-medium truncate ' + (isActive ? 'text-primary' : '')}>{track.title}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{track.artist}</p>
                            </div>
                            {track.duration > 0 && (
                              <span className="text-[10px] text-muted-foreground font-mono shrink-0">{formatDuration(track.duration)}</span>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (qIdx >= 0) radio.playTrack(qIdx);
                              }}
                              className="p-1 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors shrink-0"
                            >
                              {isThisPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                            </button>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {playlists.length === 0 && presetPlaylists.length === 0 && (
        <div className="text-center py-16 px-4">
          <ListPlus className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" />
          <p className="text-sm font-medium">No playlists yet</p>
          <p className="text-xs text-muted-foreground mt-1">Playlists are auto-generated when artists add genres to their tracks.</p>
        </div>
      )}
    </div>
  );
}

// ── Song Detail View ──────────────────────────────────────────────

function SongDetailView({ track, onBack, onEdit, onDelete, onPromote }: {
  track: RadioTrack;
  onBack: () => void;
  onEdit?: (track: RadioTrack) => void;
  onDelete?: (track: RadioTrack) => void;
  onPromote?: (track: RadioTrack) => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const radio = useRadioPlayer();
  const { profile: artistProfile } = useNostrProfile(track.submittedBy);
  const [liked, setLiked] = useState(false);
  const [comment, setComment] = useState('');
  const [commenting, setCommenting] = useState(false);
  const isOwnTrack = user?.pubkey === track.submittedBy;

  const qIdx = radio.queue.findIndex(t => t.id === track.id);
  const isActive = qIdx === radio.currentIndex;
  const isThisPlaying = isActive && radio.isPlaying;
  const likeCount = radio.likeCounts.get(track.id) || 0;
  const trackComments = radio.commentsByTrack.get(track.id) || [];
  const commentCount = trackComments.length;
  const tipTotal = radio.tipCounts.get(track.id) || 0;

  const handleLike = async () => {
    if (!user || liked) return;
    setLiked(true);
    try {
      await publish({
        kind: 7,
        content: '+',
        tags: [['e', track.id], ['p', track.submittedBy], ['t', RADIO_TRACK_TAG]],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch { setLiked(false); }
  };

  const handleComment = async () => {
    if (!user || !comment.trim() || commenting) return;
    setCommenting(true);
    try {
      await publish({
        kind: 1111,
        content: comment.trim(),
        tags: [
          // NIP-22: root scope — the track (uppercase)
          ['E', track.id, '', track.submittedBy],
          ['K', String(RADIO_TRACK_KIND)],
          ['P', track.submittedBy],
          // NIP-22: parent item (same as root for top-level comments)
          ['e', track.id, '', track.submittedBy],
          ['k', String(RADIO_TRACK_KIND)],
          ['p', track.submittedBy],
          ['t', 'bch-radio-comment'],
          ['t', RADIO_TRACK_TAG],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setComment('');
    } catch {}
    setCommenting(false);
  };

  const handleShare = () => {
    const url = `https://bchnostr.com/radio?track=${track.id}`;
    navigator.share?.({ title: `${track.title} by ${track.artist}`, text: `Listen to "${track.title}" on BCH Radio`, url }).catch(() => {
      navigator.clipboard.writeText(url).catch(() => {});
    });
  };

  const progressPct = isActive && radio.duration > 0 ? (radio.currentTime / radio.duration) * 100 : 0;

  return (
    <div className="pb-8">
      {/* Back button */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ChevronDown className="w-4 h-4 rotate-90" /> Back to Radio
        </button>
      </div>

      {/* Album art hero */}
      <div className="px-4 pt-6 pb-4 flex flex-col items-center">
        <div className={`w-48 h-48 sm:w-56 sm:h-56 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-border flex items-center justify-center overflow-hidden shadow-xl ${isThisPlaying ? 'shadow-primary/20' : ''}`}>
          {track.coverArt ? (
            <img src={track.coverArt} alt="" className="w-full h-full object-cover" />
          ) : (
            <Disc3 className={`w-20 h-20 text-primary/30 ${isThisPlaying ? 'animate-spin' : ''}`} style={isThisPlaying ? { animationDuration: '3s' } : undefined} />
          )}
        </div>

        {/* Title + Artist */}
        <div className="text-center mt-5 max-w-sm">
          <h1 className="text-xl font-bold">{track.title}</h1>
          <Link to={`/profile/${track.submittedBy}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
            {artistProfile?.display_name || artistProfile?.name || track.artist}
          </Link>
          {track.genre && (
            <span className="inline-block ml-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground">
              {track.genre}
            </span>
          )}
        </div>

        {/* Progress bar (when this song is active) */}
        {isActive && (
          <div className="w-full max-w-sm mt-4 space-y-1">
            <div className="w-full h-1.5 bg-muted rounded-full cursor-pointer" onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const pct = (e.clientX - rect.left) / rect.width;
              radio.seekTo(Math.max(0, Math.min(1, pct)));
            }}>
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground font-mono">
              <span>{formatDuration(radio.currentTime)}</span>
              <span>{formatDuration(radio.duration)}</span>
            </div>
          </div>
        )}

        {/* Playback controls */}
        <div className="flex items-center gap-4 mt-4">
          <button onClick={radio.skipPrev} className="p-2 text-muted-foreground hover:text-foreground transition-colors">
            <SkipBack className="w-5 h-5" />
          </button>
          <button
            onClick={() => {
              if (isActive) {
                radio.togglePlay();
              } else if (qIdx >= 0) {
                radio.playTrack(qIdx);
              }
            }}
            className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all shadow-lg"
          >
            {radio.loading && isActive ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : isThisPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6 ml-0.5" />
            )}
          </button>
          <button onClick={radio.skipNext} className="p-2 text-muted-foreground hover:text-foreground transition-colors">
            <SkipForward className="w-5 h-5" />
          </button>
          <button
            onClick={radio.toggleShuffle}
            className={`p-2 rounded-full transition-colors ${
              radio.shuffle
                ? 'text-primary bg-primary/10'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title={radio.shuffle ? 'Shuffle on' : 'Shuffle off'}
          >
            <Shuffle className="w-4 h-4" />
          </button>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-6 mt-5">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="w-3.5 h-3.5" />
            {track.duration > 0 ? formatDuration(track.duration) : '—'}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Heart className="w-3.5 h-3.5" />
            {likeCount}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MessageCircle className="w-3.5 h-3.5" />
            {commentCount}
          </div>
        </div>

        {/* Social Tip Proof — Earnings Banner */}
        <SocialTipProofBanner trackId={track.id} />

        {/* Action buttons */}
        <div className="flex items-center gap-3 mt-5 flex-wrap justify-center">
          <button
            onClick={handleLike}
            disabled={liked || !user}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              liked
                ? 'bg-red-500/10 text-red-500 border border-red-500/30'
                : 'bg-muted text-muted-foreground hover:text-red-500 hover:bg-red-500/10'
            }`}
          >
            <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
            {liked ? 'Liked' : 'Like'}
          </button>

          <BCHTipButton recipientPubkey={track.submittedBy} recipientProfile={artistProfile} eventId={track.id} />

          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>

          {/* Promote button — own tracks only */}
          {isOwnTrack && (
            <button
              onClick={() => onPromote?.(track)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 transition-colors"
            >
              <Rocket className="w-4 h-4" />
              Promote
            </button>
          )}
        </div>

        {/* Edit / Delete — only for own tracks */}
        {isOwnTrack && (
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={() => onEdit?.(track)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit Track
            </button>
            <button
              onClick={() => onDelete?.(track)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Artist card */}
      <div className="mx-4 mt-2 mb-4 p-4 rounded-xl border border-border bg-card">
        <Link to={`/profile/${track.submittedBy}`} className="flex items-center gap-3">
          {artistProfile?.picture ? (
            <img src={artistProfile.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary/60" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">
              {artistProfile?.display_name || artistProfile?.name || track.artist}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {artistProfile?.about?.slice(0, 80) || 'BCH Radio Artist'}
            </p>
          </div>
          <span className="text-xs text-primary font-medium">View Profile →</span>
        </Link>
      </div>

      {/* Comment section */}
      <div className="px-4">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <MessageCircle className="w-4 h-4" /> Comments ({commentCount})
        </h3>

        {/* Add comment */}
        {user && (
          <div className="flex items-center gap-2 mb-4">
            <input
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Say something about this track..."
              maxLength={300}
              onKeyDown={e => e.key === 'Enter' && handleComment()}
              className="flex-1 bg-muted rounded-xl px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={handleComment}
              disabled={!comment.trim() || commenting}
              className="p-2.5 rounded-xl bg-primary text-primary-foreground disabled:opacity-50 hover:bg-primary/90 transition-colors"
            >
              {commenting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        )}

        {trackComments.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">No comments yet. Be the first!</p>
        ) : (
          <div className="space-y-3 pb-4">
            {trackComments.map(c => (
              <RadioCommentRow key={c.id} comment={c} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Radio comment row ─────────────────────────────────────────────
// Renders a single track comment with its author's avatar + name.
function RadioCommentRow({ comment }: { comment: Event }) {
  const { profile } = useNostrProfile(comment.pubkey);
  const name = profile?.display_name || profile?.name || comment.pubkey.slice(0, 8);
  const npub = (() => { try { return nip19.npubEncode(comment.pubkey); } catch { return comment.pubkey; } })();

  return (
    <div className="flex items-start gap-2.5">
      <Link to={`/profile/${npub}`} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground">
            {name.slice(0, 2).toUpperCase()}
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <Link to={`/profile/${npub}`} className="text-xs font-semibold truncate hover:text-primary">
            {name}
          </Link>
          <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(comment.created_at)}</span>
        </div>
        <p className="text-sm text-foreground/90 whitespace-pre-wrap break-words">{comment.content}</p>
      </div>
    </div>
  );
}

// ── Social Tip Proof Helpers ──────────────────────────────────────

/** Format tip amount: show BCH if >= 0.001 BCH, otherwise show sats */
function formatTipAmount(sats: number): string {
  if (sats <= 0) return '0';
  const bch = sats / 1e8;
  if (bch >= 0.01) return `${bch.toFixed(4)} BCH`;
  if (bch >= 0.001) return `${bch.toFixed(6)} BCH`;
  return `${sats.toLocaleString()} sats`;
}

/** Format tip amount with USD value */
function formatTipAmountWithUsd(sats: number, bchPrice: number | null): { bchStr: string; usdStr: string | null } {
  if (sats <= 0) return { bchStr: '0', usdStr: null };
  const bch = sats / 1e8;
  const bchStr = bch >= 0.001 ? `${bch.toFixed(bch >= 0.01 ? 4 : 6)} BCH` : `${sats.toLocaleString()} sats`;
  const usdStr = bchPrice && bchPrice > 0 ? `$${(bch * bchPrice).toFixed(2)}` : null;
  return { bchStr, usdStr };
}

/** Inline social tip proof badge for NowPlayingHero track info */
function SocialTipProof({ trackId }: { trackId: string }) {
  const radio = useRadioPlayer();
  const tipSats = radio.tipCounts.get(trackId) || 0;
  if (tipSats <= 0) return null;

  const bchPrice = getBCHPrice();
  const { bchStr, usdStr } = formatTipAmountWithUsd(tipSats, bchPrice);

  return (
    <div className="flex items-center gap-1.5 mt-1.5">
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
        <Coins className="w-3 h-3 text-emerald-500" />
        <span className="text-[10px] font-bold text-emerald-500">{bchStr}</span>
        {usdStr && (
          <span className="text-[10px] text-emerald-400/70 font-medium">({usdStr})</span>
        )}
        <span className="text-[9px] text-emerald-500/60 font-medium">earned</span>
      </div>
    </div>
  );
}

/** Larger social tip proof banner for SongDetailView */
function SocialTipProofBanner({ trackId }: { trackId: string }) {
  const radio = useRadioPlayer();
  const tipSats = radio.tipCounts.get(trackId) || 0;

  const bchPrice = getBCHPrice();
  const bch = tipSats / 1e8;
  const usd = bchPrice && bchPrice > 0 ? bch * bchPrice : 0;

  return (
    <div className="w-full max-w-sm mt-4">
      <div className={`rounded-xl border overflow-hidden ${tipSats > 0 ? 'border-emerald-500/30 bg-gradient-to-r from-emerald-500/5 via-emerald-500/10 to-emerald-500/5' : 'border-border bg-muted/30'}`}>
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tipSats > 0 ? 'bg-emerald-500/15' : 'bg-muted'}`}>
              <Coins className={`w-4 h-4 ${tipSats > 0 ? 'text-emerald-500' : 'text-muted-foreground/40'}`} />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground font-medium">BCH Earned</p>
              {tipSats > 0 ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold text-emerald-500">
                    {bch >= 0.001 ? `${bch.toFixed(bch >= 0.01 ? 4 : 6)} BCH` : `${tipSats.toLocaleString()} sats`}
                  </span>
                  {usd >= 0.01 && (
                    <span className="text-xs text-emerald-400/70 font-medium">(${usd.toFixed(2)})</span>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground/60">No tips yet — be the first!</p>
              )}
            </div>
          </div>
          {tipSats > 0 && (
            <div className="text-right">
              <p className="text-[9px] text-emerald-500/70 font-medium uppercase tracking-wider">Verified on-chain</p>
              <p className="text-[9px] text-muted-foreground mt-0.5">{tipSats.toLocaleString()} sats total</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Now Playing Hero ─────────────────────────────────────────────

function NowPlayingHero({
  track,
  isPlaying,
  loading,
  currentTime,
  duration,
  volume,
  muted,
  onTogglePlay,
  onSkipNext,
  onSkipPrev,
  onSeek,
  onVolumeChange,
  onToggleMute,
  queueLength,
  currentIndex,
  onTrackClick,
}: {
  track: RadioTrack | null;
  isPlaying: boolean;
  loading: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  onTogglePlay: () => void;
  onSkipNext: () => void;
  onSkipPrev: () => void;
  onSeek: (pct: number) => void;
  onVolumeChange: (v: number) => void;
  onToggleMute: () => void;
  queueLength: number;
  currentIndex: number;
  onTrackClick: (track: RadioTrack) => void;
}) {
  const { profile: submitterProfile } = useNostrProfile(track?.submittedBy || '');
  const radio = useRadioPlayer();
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const bar = progressBarRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(pct);
  }, [onSeek]);

  return (
    <div className="bg-gradient-to-b from-primary/5 via-background to-background border-b border-border">
      <div className="px-4 py-6">
        {/* Album art + info */}
        <div className="flex items-center gap-4 mb-5">
          {/* Cover art / spinning disc */}
          <button
            onClick={() => track && onTrackClick(track)}
            className={`w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-border flex items-center justify-center shrink-0 overflow-hidden ${isPlaying ? 'animate-pulse' : ''}`}
          >
            {track?.coverArt ? (
              <img src={track.coverArt} alt="" className="w-full h-full object-cover" />
            ) : (
              <Disc3 className={`w-10 h-10 text-primary/40 ${isPlaying ? 'animate-spin' : ''}`} style={isPlaying ? { animationDuration: '3s' } : undefined} />
            )}
          </button>

          {/* Track info */}
          <div className="flex-1 min-w-0">
            {track ? (
              <>
                <button onClick={() => onTrackClick(track)} className="text-left">
                  <h2 className="font-bold text-base truncate hover:text-primary transition-colors">{track.title}</h2>
                </button>
                <p className="text-sm text-muted-foreground truncate">{track.artist}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  {track.genre && <span className="inline-block px-2 py-0.5 rounded-full text-[9px] font-medium bg-muted text-muted-foreground">{track.genre}</span>}
                  {radio.shuffle && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-primary/10 text-primary border border-primary/20">
                      <Shuffle className="w-2.5 h-2.5" /> Shuffle
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  {submitterProfile && (
                    <Link to={`/profile/${track.submittedBy}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
                      {submitterProfile.picture && (
                        <img src={submitterProfile.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
                      )}
                      <span>{submitterProfile.display_name || submitterProfile.name || track.submittedBy.slice(0, 8)}</span>
                    </Link>
                  )}
                  {/* Like count */}
                  {(radio.likeCounts.get(track.id) || 0) > 0 && (
                    <span className="flex items-center gap-0.5 text-[10px] text-red-500">
                      <Heart className="w-3 h-3" /> {radio.likeCounts.get(track.id)}
                    </span>
                  )}
                </div>
                {/* Social Tip Proof — BCH earned badge */}
                <SocialTipProof trackId={track.id} />
                {/* Tip the artist */}
                {track.submittedBy && (
                  <div className="mt-2">
                    <BCHTipButton recipientPubkey={track.submittedBy} recipientProfile={submitterProfile} eventId={track.id} />
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 className="font-bold text-base text-muted-foreground">No track playing</h2>
                <p className="text-sm text-muted-foreground">Add a track to get started</p>
              </>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div
            ref={progressBarRef}
            onClick={handleProgressClick}
            className="w-full h-1.5 bg-muted rounded-full cursor-pointer group relative"
          >
            <div
              className="h-full bg-primary rounded-full transition-all relative"
              style={{ width: `${progressPct}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] text-muted-foreground font-mono">
            <span>{formatDuration(currentTime)}</span>
            <span>{formatDuration(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-3 mt-3">
          {/* Volume */}
          <button
            onClick={onToggleMute}
            className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>

          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={muted ? 0 : volume}
            onChange={e => onVolumeChange(parseFloat(e.target.value))}
            className="w-16 h-1 accent-primary"
          />

          {/* Skip back */}
          <button
            onClick={onSkipPrev}
            disabled={currentIndex <= 0 && currentTime <= 3}
            className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-30"
          >
            <SkipBack className="w-5 h-5" />
          </button>

          {/* Play/Pause */}
          <button
            onClick={onTogglePlay}
            disabled={queueLength === 0}
            className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-40 shadow-lg"
          >
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6 ml-0.5" />
            )}
          </button>

          {/* Skip forward */}
          <button
            onClick={onSkipNext}
            disabled={!radio.shuffle && currentIndex >= queueLength - 1}
            className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-30"
          >
            <SkipForward className="w-5 h-5" />
          </button>

          {/* Shuffle toggle */}
          <button
            onClick={radio.toggleShuffle}
            className={`p-2 rounded-full transition-colors ${
              radio.shuffle
                ? 'text-primary bg-primary/10 hover:bg-primary/20'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            title={radio.shuffle ? 'Shuffle on' : 'Shuffle off'}
          >
            <Shuffle className="w-4 h-4" />
          </button>

          {/* Queue position */}
          <span className="text-[10px] text-muted-foreground font-mono min-w-[40px] text-center">
            {queueLength > 0 ? `${currentIndex + 1}/${queueLength}` : '—'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Track Row ────────────────────────────────────────────────────

function TrackRow({
  track,
  index,
  isActive,
  isPlaying,
  onPlay,
  onTrackClick,
  onEdit,
  onDelete,
}: {
  track: RadioTrack;
  index: number;
  isActive: boolean;
  isPlaying: boolean;
  onPlay: () => void;
  onTrackClick?: () => void;
  onEdit?: (track: RadioTrack) => void;
  onDelete?: (track: RadioTrack) => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const radio = useRadioPlayer();
  const { profile } = useNostrProfile(track.submittedBy);
  const submitterName = profile?.display_name || profile?.name || track.submittedBy.slice(0, 8);
  const [showReport, setShowReport] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isOwnTrack = user?.pubkey === track.submittedBy;
  const isOperator = user?.pubkey === OPERATOR_PUBKEY;

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);
  const likeCount = radio.likeCounts.get(track.id) || 0;
  const tipSats = radio.tipCounts.get(track.id) || 0;

  const handleReport = async () => {
    if (!user || reporting || reported) return;
    setReporting(true);
    try {
      await publish({
        kind: FLAG_KIND,
        content: 'copyright',
        tags: [
          ['e', track.id],
          ['p', track.submittedBy],
          ['t', FLAG_TAG],
          ['t', 'bch-radio-flag'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setReported(true);
      setShowReport(false);
    } catch {}
    setReporting(false);
  };

  const handleOperatorRemove = async () => {
    if (!user || !isOperator || removing) return;
    setRemoving(true);
    try {
      await publish({
        kind: FLAG_KIND,
        content: 'copyright-removal',
        tags: [
          ['e', track.id],
          ['p', track.submittedBy],
          ['t', FLAG_TAG],
          ['t', 'bch-radio-flag'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setRemoved(true);
      if (isActive) setTimeout(() => radio.skipNext(), 500);
      setTimeout(() => radio.refetchQueue(), 1500);
    } catch {}
    setRemoving(false);
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 border-b border-border cursor-pointer transition-colors group ${
        isActive
          ? 'bg-primary/5 border-l-2 border-l-primary'
          : 'hover:bg-muted/30'
      }`}
    >
      {/* Track number / play indicator */}
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" onClick={onPlay}>
        {isActive && isPlaying ? (
          <div className="flex items-end gap-0.5 h-4">
            <div className="w-1 bg-primary rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
            <div className="w-1 bg-primary rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
            <div className="w-1 bg-primary rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
          </div>
        ) : isActive ? (
          <Pause className="w-4 h-4 text-primary" />
        ) : (
          <span className="text-xs text-muted-foreground font-mono">{index + 1}</span>
        )}
      </div>

      {/* Cover art thumbnail */}
      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden" onClick={onPlay}>
        {track.coverArt ? (
          <img src={track.coverArt} alt="" className="w-full h-full object-cover" />
        ) : (
          <Music className="w-4 h-4 text-muted-foreground/40" />
        )}
      </div>

      {/* Track info */}
      <div className="flex-1 min-w-0" onClick={onTrackClick || onPlay}>
        <p className={`text-sm font-medium truncate ${isActive ? 'text-primary' : ''}`}>
          {track.title}
        </p>
        <div className="flex items-center gap-2">
          <p className="text-xs text-muted-foreground truncate">{track.artist}</p>
          {track.genre && <span className="text-[9px] text-muted-foreground/60">{track.genre}</span>}
        </div>
        {/* Social stats inline */}
        {(likeCount > 0 || tipSats > 0) && (
          <div className="flex items-center gap-2 mt-0.5">
            {likeCount > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] text-red-500">
                <Heart className="w-2.5 h-2.5" /> {likeCount}
              </span>
            )}
            {tipSats > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] text-emerald-500 font-medium" title={`${tipSats.toLocaleString()} sats earned in BCH tips`}>
                <Coins className="w-2.5 h-2.5" /> {formatTipAmount(tipSats)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Submitter + duration + report */}
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to={`/profile/${track.submittedBy}`}
          onClick={e => e.stopPropagation()}
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors"
        >
          {profile?.picture && (
            <img src={profile.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
          )}
          <span className="hidden sm:inline max-w-[60px] truncate">{submitterName}</span>
        </Link>
        {track.duration > 0 && (
          <span className="text-[10px] text-muted-foreground font-mono">
            {formatDuration(track.duration)}
          </span>
        )}

        {/* Own track: edit/delete menu */}
        {isOwnTrack && !removed && (
          <div className="relative" ref={menuRef} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-lg text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover:opacity-100"
              title="Track options"
            >
              <MoreVertical className="w-3.5 h-3.5" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 z-20 w-36 bg-card border border-border rounded-xl shadow-xl overflow-hidden">
                <button
                  onClick={() => { setShowMenu(false); onEdit?.(track); }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-medium hover:bg-muted transition-colors text-left"
                >
                  <Pencil className="w-3.5 h-3.5 text-primary" />
                  Edit Track
                </button>
                <button
                  onClick={() => { setShowMenu(false); onDelete?.(track); }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-xs font-medium hover:bg-red-500/10 text-red-500 transition-colors text-left"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete Track
                </button>
              </div>
            )}
          </div>
        )}

        {/* Operator remove button */}
        {isOperator && !isOwnTrack && !removed && (
          <div className="relative" onClick={e => e.stopPropagation()}>
            <button
              onClick={handleOperatorRemove}
              disabled={removing}
              className="p-1.5 rounded-lg text-red-500/70 hover:text-red-500 hover:bg-red-500/10 transition-colors"
              title="Remove track (admin)"
            >
              {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
        {removed && (
          <span className="text-[10px] text-red-500 font-medium" onClick={e => e.stopPropagation()}>Removed</span>
        )}

        {/* Report / Flag button — regular users */}
        {user && !isOwnTrack && !isOperator && (
          <div className="relative" onClick={e => e.stopPropagation()}>
            {reported ? (
              <span className="text-[10px] text-amber-500 font-medium">Reported</span>
            ) : showReport ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={handleReport}
                  disabled={reporting}
                  className="px-2 py-1 rounded-md bg-red-500 text-white text-[10px] font-medium hover:bg-red-600 disabled:opacity-50"
                >
                  {reporting ? '...' : 'Confirm'}
                </button>
                <button
                  onClick={() => setShowReport(false)}
                  className="px-2 py-1 rounded-md border border-border text-[10px] hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowReport(true)}
                className="p-1 rounded-lg text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/10 transition-colors opacity-0 group-hover:opacity-100"
                title="Report copyright violation"
              >
                <Flag className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Promote Song Modal (Auction-Style, USD) ─────────────────────────

function PromoteSongModal({
  track,
  onClose,
  onPromoted,
}: {
  track: RadioTrack;
  onClose: () => void;
  onPromoted: () => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const radio = useRadioPlayer();
  const [bidInput, setBidInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [bchPrice, setBchPrice] = useState(0);

  useEffect(() => {
    const price = getBCHPrice();
    if (price) setBchPrice(price);
  }, []);

  const auction = radio.songPromoAuction;
  const minBidSats = auction.minimumBidSats;
  const minBidUsd = bchPrice > 0 ? satsToUsd(minBidSats, bchPrice) : 0;
  // Round up to nearest cent for display
  const minBidUsdDisplay = Math.max(0.01, Math.ceil(minBidUsd * 100) / 100);

  const bidUsd = parseFloat(bidInput) || 0;
  const bidSats = bchPrice > 0 ? usdToSats(bidUsd, bchPrice) : 0;
  const bidValid = bidSats >= minBidSats && bidUsd > 0;

  // Check if this track is already promoted
  const alreadyPromoted = auction.bids.some(b => b.trackId === track.id);

  // Lowest bid in USD for display
  const lowestBidUsd = auction.lowestBid && bchPrice > 0 ? satsToUsd(auction.lowestBid.paidSats, bchPrice) : 0;

  const handleBid = async () => {
    if (!user || !tipWallet || submitting || !bidValid) return;
    setSubmitting(true);
    setError('');

    try {
      let txid = '';
      if (bidSats > 0) {
        txid = await tipWallet.sendTipWithFee(PLATFORM_FEE_ADDRESS, BigInt(bidSats));
        if (!txid) throw new Error('Payment failed — no transaction ID returned');
      }

      const now = Math.floor(Date.now() / 1000);
      const promoData = {
        type: 'song',
        trackId: track.id,
        expiresAt: now + PROMO_AUCTION_DURATION,
        paidSats: bidSats,
        txid,
      };

      await publish({
        kind: RADIO_PROMO_KIND,
        content: JSON.stringify(promoData),
        tags: [
          ['d', PROMO_D_TAG_PREFIX + 'song-' + track.id + '-' + now],
          ['t', RADIO_PROMO_TAG],
          ['t', 'bch-radio'],
          ['e', track.id],
        ],
        created_at: now,
      });

      onPromoted();
    } catch (err: any) {
      setError(err?.message || 'Bid failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-sm shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Rocket className="w-4.5 h-4.5 text-amber-500" />
            Promote Song
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {/* Track preview */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border">
            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden shrink-0">
              {track.coverArt ? <img src={track.coverArt} alt="" className="w-full h-full object-cover" /> : <Music className="w-5 h-5 text-muted-foreground/40" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{track.title}</p>
              <p className="text-xs text-muted-foreground truncate">{track.artist}</p>
            </div>
          </div>

          {/* What you get */}
          <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-3">
            <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1.5">Your song will appear in:</p>
            <ul className="space-y-1">
              <li className="flex items-center gap-2 text-[11px] text-muted-foreground"><Megaphone className="w-3 h-3 text-amber-500 shrink-0" /> Featured Songs section for 7 days</li>
              <li className="flex items-center gap-2 text-[11px] text-muted-foreground"><Sparkles className="w-3 h-3 text-amber-500 shrink-0" /> Higher visibility in Discover</li>
              <li className="flex items-center gap-2 text-[11px] text-muted-foreground"><BadgeCheck className="w-3 h-3 text-amber-500 shrink-0" /> "Promoted" badge on your track</li>
            </ul>
          </div>

          {/* Auction Status */}
          <div className="rounded-xl bg-muted/50 border border-border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Slots Used</span>
              <span className="text-xs font-bold">{auction.slotsUsed} / {PROMO_MAX_SONG_SLOTS}</span>
            </div>
            {auction.slotsFull && auction.lowestBid && (
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Lowest Current Bid</span>
                <span className="text-xs font-bold text-amber-600 dark:text-amber-400">{lowestBidUsd > 0 ? formatUsd(lowestBidUsd) : `${auction.lowestBid.paidSats.toLocaleString()} sats`}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Minimum Bid</span>
              <span className="text-xs font-bold text-primary">{bchPrice > 0 ? formatUsd(minBidUsdDisplay) : `${minBidSats.toLocaleString()} sats`}</span>
            </div>
            {auction.slotsFull && (
              <p className="text-[10px] text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-lg px-2 py-1.5">
                All {PROMO_MAX_SONG_SLOTS} slots are full. Your bid must beat the lowest to claim a slot. The outbid song loses its Featured spot.
              </p>
            )}
          </div>

          {alreadyPromoted && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 rounded-lg px-3 py-2 flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 shrink-0" />
              This track is already promoted! You can increase your bid to stay competitive.
            </p>
          )}

          {/* Bid input in USD */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Your Bid (USD)</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <input
                type="number"
                min={minBidUsdDisplay || 0.01}
                step="0.01"
                value={bidInput}
                onChange={e => setBidInput(e.target.value)}
                placeholder={bchPrice > 0 ? `Min: ${minBidUsdDisplay.toFixed(2)}` : '0.00'}
                className="w-full pl-7 pr-4 py-2.5 rounded-xl border border-border bg-background text-sm font-mono focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 transition-colors"
              />
            </div>
            {bidInput && !bidValid && bchPrice > 0 && (
              <p className="text-[10px] text-red-500">Minimum bid is {formatUsd(minBidUsdDisplay)}</p>
            )}
            {bchPrice === 0 && (
              <p className="text-[10px] text-amber-600">Loading BCH price...</p>
            )}
            {/* Quick bid buttons */}
            {bchPrice > 0 && (
              <div className="flex gap-1.5">
                {[minBidUsdDisplay, Math.ceil(minBidUsdDisplay * 2 * 100) / 100, Math.ceil(minBidUsdDisplay * 5 * 100) / 100, Math.ceil(minBidUsdDisplay * 10 * 100) / 100].map((amt, i) => (
                  <button
                    key={i}
                    onClick={() => setBidInput(amt.toFixed(2))}
                    className="flex-1 px-2 py-1.5 rounded-lg border border-border text-[10px] font-medium hover:border-amber-500/40 hover:bg-amber-500/5 transition-colors"
                  >
                    {formatUsd(amt)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {!tipWallet && (
            <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2">
              You need a BCH wallet connected to promote songs. Set one up in Settings.
            </p>
          )}

          {error && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}

          <button
            onClick={handleBid}
            disabled={submitting || !user || !tipWallet || !bidValid}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
            ) : (
              <><Rocket className="w-4 h-4" /> Place Bid — {bidUsd > 0 ? formatUsd(bidUsd) : 'Enter amount'}</>
            )}
          </button>

          <p className="text-[10px] text-muted-foreground text-center">
            Paid in BCH at current market rate. Promoted for 7 days. Higher bids keep their slot. No refunds.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Featured Artist Modal (Auction-Style, USD) ─────────────────────

function FeaturedArtistModal({
  onClose,
  onPromoted,
}: {
  onClose: () => void;
  onPromoted: () => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const radio = useRadioPlayer();
  const [bidInput, setBidInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [bchPrice, setBchPrice] = useState(0);

  useEffect(() => {
    const price = getBCHPrice();
    if (price) setBchPrice(price);
  }, []);

  const auction = radio.featuredArtistAuction;
  const minBidSats = auction.minimumBidSats;
  const minBidUsd = bchPrice > 0 ? satsToUsd(minBidSats, bchPrice) : 0;
  const minBidUsdDisplay = Math.max(0.01, Math.ceil(minBidUsd * 100) / 100);

  const bidUsd = parseFloat(bidInput) || 0;
  const bidSats = bchPrice > 0 ? usdToSats(bidUsd, bchPrice) : 0;
  const bidValid = bidSats >= minBidSats && bidUsd > 0;
  const currentHolderPubkey = auction.currentBid?.pubkey;

  // Current holder profile
  const { profile: currentHolderProfile } = useNostrProfile(currentHolderPubkey || '');
  const currentHolderName = currentHolderProfile?.display_name || currentHolderProfile?.name || currentHolderPubkey?.slice(0, 8) || '';

  // Current winning bid in USD
  const winningBidUsd = auction.currentBid && bchPrice > 0 ? satsToUsd(auction.currentBid.paidSats, bchPrice) : 0;

  const handleBid = async () => {
    if (!user || !tipWallet || submitting || !bidValid) return;
    setSubmitting(true);
    setError('');

    try {
      let txid = '';
      if (bidSats > 0) {
        txid = await tipWallet.sendTipWithFee(PLATFORM_FEE_ADDRESS, BigInt(bidSats));
        if (!txid) throw new Error('Payment failed — no transaction ID returned');
      }

      const now = Math.floor(Date.now() / 1000);
      const promoData = {
        type: 'artist',
        trackId: '',
        expiresAt: now + PROMO_AUCTION_DURATION,
        paidSats: bidSats,
        txid,
      };

      await publish({
        kind: RADIO_PROMO_KIND,
        content: JSON.stringify(promoData),
        tags: [
          ['d', PROMO_D_TAG_PREFIX + 'artist-' + user.pubkey + '-' + now],
          ['t', RADIO_PROMO_TAG],
          ['t', 'bch-radio'],
        ],
        created_at: now,
      });

      onPromoted();
    } catch (err: any) {
      setError(err?.message || 'Bid failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-sm shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Crown className="w-4.5 h-4.5 text-amber-500" />
            Featured Artist Auction
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {/* What you get */}
          <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-3">
            <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1.5">As the Featured Artist, you get:</p>
            <ul className="space-y-1">
              <li className="flex items-center gap-2 text-[11px] text-muted-foreground"><Crown className="w-3 h-3 text-amber-500 shrink-0" /> Premium banner at the top of BCH Radio for 7 days</li>
              <li className="flex items-center gap-2 text-[11px] text-muted-foreground"><Users className="w-3 h-3 text-amber-500 shrink-0" /> Direct link to your profile and music</li>
              <li className="flex items-center gap-2 text-[11px] text-muted-foreground"><Sparkles className="w-3 h-3 text-amber-500 shrink-0" /> Maximum visibility to all listeners</li>
            </ul>
          </div>

          {/* Current Auction Status */}
          <div className="rounded-xl bg-muted/50 border border-border p-3 space-y-2">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide">Current Auction Status</p>
            {auction.currentBid ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Current Holder</span>
                  <span className="text-xs font-bold truncate max-w-[140px]">{currentHolderName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Winning Bid</span>
                  <span className="text-xs font-bold text-amber-600 dark:text-amber-400">{winningBidUsd > 0 ? formatUsd(winningBidUsd) : `${auction.currentBid.paidSats.toLocaleString()} sats`}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Time Remaining</span>
                  <span className="text-xs font-bold">{auction.hoursLeft}h left</span>
                </div>
                <p className="text-[10px] text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-lg px-2 py-1.5">
                  Outbid the current holder to claim the Featured Artist spot!
                </p>
              </>
            ) : (
              <div className="flex items-center gap-2 py-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Slot is open — be the first to claim it!</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1 border-t border-border/50">
              <span className="text-xs font-medium text-muted-foreground">Minimum Bid</span>
              <span className="text-xs font-bold text-primary">{bchPrice > 0 ? formatUsd(minBidUsdDisplay) : `${minBidSats.toLocaleString()} sats`}</span>
            </div>
          </div>

          {/* Bid input in USD */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Your Bid (USD)</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <input
                type="number"
                min={minBidUsdDisplay || 0.01}
                step="0.01"
                value={bidInput}
                onChange={e => setBidInput(e.target.value)}
                placeholder={bchPrice > 0 ? `Min: ${minBidUsdDisplay.toFixed(2)}` : '0.00'}
                className="w-full pl-7 pr-4 py-2.5 rounded-xl border border-border bg-background text-sm font-mono focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 transition-colors"
              />
            </div>
            {bidInput && !bidValid && bchPrice > 0 && (
              <p className="text-[10px] text-red-500">Minimum bid is {formatUsd(minBidUsdDisplay)}</p>
            )}
            {bchPrice === 0 && (
              <p className="text-[10px] text-amber-600">Loading BCH price...</p>
            )}
            {/* Quick bid buttons */}
            {bchPrice > 0 && (
              <div className="flex gap-1.5">
                {[minBidUsdDisplay, Math.ceil(minBidUsdDisplay * 2 * 100) / 100, Math.ceil(minBidUsdDisplay * 5 * 100) / 100, Math.ceil(minBidUsdDisplay * 10 * 100) / 100].map((amt, i) => (
                  <button
                    key={i}
                    onClick={() => setBidInput(amt.toFixed(2))}
                    className="flex-1 px-2 py-1.5 rounded-lg border border-border text-[10px] font-medium hover:border-amber-500/40 hover:bg-amber-500/5 transition-colors"
                  >
                    {formatUsd(amt)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {!tipWallet && (
            <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2">
              You need a BCH wallet connected to become a Featured Artist. Set one up in Settings.
            </p>
          )}

          {error && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}

          <button
            onClick={handleBid}
            disabled={submitting || !user || !tipWallet || !bidValid}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
            ) : (
              <><Crown className="w-4 h-4" /> {auction.currentBid ? 'Outbid' : 'Claim Spot'} — {bidUsd > 0 ? formatUsd(bidUsd) : 'Enter amount'}</>
            )}
          </button>

          <p className="text-[10px] text-muted-foreground text-center">
            Paid in BCH at current market rate. Highest bidder gets the Featured Artist banner for 7 days. No refunds.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Edit Track Modal ──────────────────────────────────────────────

function EditTrackModal({
  track,
  onClose,
  onSaved,
}: {
  track: RadioTrack;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();

  const [title, setTitle] = useState(track.title);
  const [artist, setArtist] = useState(track.artist);
  const [genre, setGenre] = useState<string>(track.genre || '');
  const [audioUrl, setAudioUrl] = useState(track.url);
  const [coverArtUrl, setCoverArtUrl] = useState(track.coverArt || '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [audioDuration, setAudioDuration] = useState(track.duration);

  const coverFileRef = useRef<HTMLInputElement>(null);
  const audioFileRef = useRef<HTMLInputElement>(null);

  // Re-detect audio duration if URL changes
  useEffect(() => {
    if (!audioUrl || audioUrl === track.url) return;
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.src = audioUrl;
    audio.onloadedmetadata = () => setAudioDuration(Math.round(audio.duration));
    audio.onerror = () => {};
    return () => { audio.src = ''; };
  }, [audioUrl, track.url]);

  const handleAudioUpload = async (file: File) => {
    if (!isAudioFile(file)) {
      setError('Please select an audio file (MP3, M4A, OGG, WAV, FLAC, etc.)');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setError('File is too large (max 100MB)');
      return;
    }
    setUploading(true);
    setUploadProgress('Uploading audio...');
    setError('');
    try {
      const result = await uploadMedia(file);
      setAudioUrl(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const handleCoverUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    setUploading(true);
    setUploadProgress('Uploading cover art...');
    try {
      const result = await uploadMedia(file);
      setCoverArtUrl(result.url);
    } catch {
      setError('Cover art upload failed');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const handleSave = async () => {
    if (!user || !audioUrl || !title.trim()) return;
    setSubmitting(true);
    setError('');

    try {
      // Step 1: Publish a NIP-09 deletion request for the old event
      await publish({
        kind: 5,
        content: 'Track edited — replaced with updated version',
        tags: [
          ['e', track.id],
          ['k', String(RADIO_TRACK_KIND)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      // Step 2: Publish the new track event with updated metadata
      const trackData = {
        url: audioUrl,
        title: title.trim(),
        artist: artist.trim() || 'Unknown',
        duration: audioDuration,
        paidSats: 0,
        coverArt: coverArtUrl || undefined,
        genre: genre || undefined,
      };

      const tags: string[][] = [
        ['t', RADIO_TRACK_TAG],
        ['t', 'bch'],
        ['t', 'bch-radio'],
      ];
      if (genre && genre !== 'All') {
        tags.push(['t', `bch-radio-genre-${genre.toLowerCase().replace(/[^a-z0-9]/g, '-')}`]);
      }

      await publish({
        kind: RADIO_TRACK_KIND,
        content: JSON.stringify(trackData),
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSubmitting(false);
    }
  };

  const hasChanges = title !== track.title ||
    artist !== track.artist ||
    genre !== (track.genre || '') ||
    audioUrl !== track.url ||
    coverArtUrl !== (track.coverArt || '');

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Pencil className="w-4.5 h-4.5 text-primary" />
            Edit Track
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {/* Audio file */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Audio File</label>
            <div className="mt-1.5 flex items-center gap-2 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
              <Music className="w-5 h-5 text-emerald-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 truncate">
                  {audioUrl === track.url ? 'Current audio' : 'New audio uploaded'}
                </p>
                {audioDuration > 0 && (
                  <p className="text-[10px] text-muted-foreground">Duration: {formatDuration(audioDuration)}</p>
                )}
              </div>
              <label className="px-2 py-1 rounded-md text-[10px] font-medium bg-muted hover:bg-muted/80 cursor-pointer transition-colors">
                Replace
                <input
                  ref={audioFileRef}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleAudioUpload(file);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            {uploading && uploadProgress.includes('audio') && (
              <div className="flex items-center gap-2 mt-2 text-xs text-primary">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {uploadProgress}
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Song Title *</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Song name"
              maxLength={100}
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Artist */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Artist</label>
            <input
              value={artist}
              onChange={e => setArtist(e.target.value)}
              placeholder="Artist or creator name"
              maxLength={100}
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Genre */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Genre</label>
            <select
              value={genre}
              onChange={e => setGenre(e.target.value)}
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">Select genre...</option>
              {GENRES.filter(g => g !== 'All').map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          {/* Cover art */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Cover Art</label>
            {coverArtUrl ? (
              <div className="mt-1.5 relative inline-block">
                <img src={coverArtUrl} alt="" className="w-20 h-20 rounded-xl object-cover" />
                <button
                  onClick={() => setCoverArtUrl('')}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="mt-1.5 flex items-center justify-center gap-2 w-20 h-20 rounded-xl border-2 border-dashed border-border hover:border-primary/40 cursor-pointer transition-colors">
                <input
                  ref={coverFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleCoverUpload(file);
                    e.target.value = '';
                  }}
                />
                {uploading && uploadProgress.includes('cover') ? (
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                ) : (
                  <Plus className="w-5 h-5 text-muted-foreground" />
                )}
              </label>
            )}
          </div>

          {/* Info notice */}
          <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Saving will delete the original track and publish an updated version. Existing likes and tips will remain linked to the original event ID and won't carry over to the new version.
              </p>
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>
          )}

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!audioUrl || !title.trim() || submitting || uploading || !hasChanges}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
              ) : (
                <><Pencil className="w-4 h-4" /> Save Changes</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Delete Track Confirmation ─────────────────────────────────────

function DeleteTrackModal({
  track,
  onClose,
  onDeleted,
}: {
  track: RadioTrack;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { publish } = useNostrPublish();
  const radio = useRadioPlayer();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      // Publish NIP-09 deletion request
      await publish({
        kind: 5,
        content: 'Track removed by author',
        tags: [
          ['e', track.id],
          ['k', String(RADIO_TRACK_KIND)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      // If this track is currently playing, skip to next
      const qIdx = radio.queue.findIndex(t => t.id === track.id);
      if (qIdx === radio.currentIndex) {
        setTimeout(() => radio.skipNext(), 300);
      }

      // Refetch queue after a short delay
      setTimeout(() => radio.refetchQueue(), 1500);

      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete track');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 pt-5 pb-4 text-center">
          {/* Track preview */}
          <div className="w-16 h-16 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-3">
            <Trash2 className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-base font-bold">Delete Track?</h2>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
            Are you sure you want to delete <strong className="text-foreground">"{track.title}"</strong>?
          </p>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            This publishes a deletion request to relays. The track will be hidden from BCH Radio. This action cannot be undone.
          </p>

          {error && (
            <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2 mt-3">{error}</p>
          )}
        </div>

        <div className="flex gap-2 px-5 pb-5">
          <button
            onClick={onClose}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-500 text-white font-medium text-sm hover:bg-red-600 disabled:opacity-50 transition-colors"
          >
            {deleting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</>
            ) : (
              <><Trash2 className="w-4 h-4" /> Delete</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Submit Track Modal ───────────────────────────────────────────

function SubmitTrackModal({
  onClose,
  onSubmitted,
}: {
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();

  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [genre, setGenre] = useState<string>('');
  const [audioUrl, setAudioUrl] = useState('');
  // MIME type of the uploaded audio (e.g. "audio/mpeg"). Blossom URLs are
  // hash-based and usually have no file extension, so the feed's NoteContent
  // renderer can't tell it's audio from the URL alone. We stash the mime here
  // and attach it as a NIP-94 `imeta` tag on the feed post so the inline
  // audio player renders right in the feed.
  const [audioMime, setAudioMime] = useState('');
  const [coverArtUrl, setCoverArtUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [audioDuration, setAudioDuration] = useState(0);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [postToFeed, setPostToFeed] = useState(true);

  const audioFileRef = useRef<HTMLInputElement>(null);
  const coverFileRef = useRef<HTMLInputElement>(null);

  // Get audio duration from uploaded file
  useEffect(() => {
    if (!audioUrl) { setAudioDuration(0); return; }
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.src = audioUrl;
    audio.onloadedmetadata = () => {
      setAudioDuration(Math.round(audio.duration));
    };
    audio.onerror = () => setAudioDuration(0);
    return () => { audio.src = ''; };
  }, [audioUrl]);

  const handleAudioUpload = async (file: File) => {
    if (!isAudioFile(file)) {
      setError('Please select an audio file (MP3, M4A, OGG, WAV, FLAC, etc.)');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setError('File is too large (max 100MB)');
      return;
    }
    setUploading(true);
    setUploadProgress('Uploading audio...');
    setError('');
    try {
      const result = await uploadMedia(file);
      setAudioUrl(result.url);
      setAudioMime(file.type || 'audio/mpeg');
      if (!title) {
        const name = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
        setTitle(name);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const handleCoverUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    setUploading(true);
    setUploadProgress('Uploading cover art...');
    try {
      const result = await uploadMedia(file);
      setCoverArtUrl(result.url);
    } catch {
      setError('Cover art upload failed');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const handleSubmit = async () => {
    if (!user || !audioUrl || !title.trim()) return;
    setSubmitting(true);
    setError('');

    try {
      const trackData = {
        url: audioUrl,
        title: title.trim(),
        artist: artist.trim() || 'Unknown',
        duration: audioDuration,
        paidSats: 0,
        coverArt: coverArtUrl || undefined,
        genre: genre || undefined,
      };

      const tags: string[][] = [
        ['t', RADIO_TRACK_TAG],
        ['t', 'bch'],
        ['t', 'bch-radio'],
      ];
      if (genre && genre !== 'All') {
        tags.push(['t', `bch-radio-genre-${genre.toLowerCase().replace(/[^a-z0-9]/g, '-')}`]);
      }

      await publish({
        kind: RADIO_TRACK_KIND,
        content: JSON.stringify(trackData),
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      // Auto-post to feed
      if (postToFeed) {
        // Put the audio URL directly in the note so the feed's NoteContent
        // renderer turns it into an inline audio player — users can hit play
        // right from the feed instead of navigating to the Radio page. The
        // cover art (if any) follows so it renders as an inline image.
        const mime = audioMime || 'audio/mpeg';
        const feedContent = `\uD83C\uDFB5 New track on BCH Radio!\n\n"${title.trim()}" by ${artist.trim() || 'Unknown'}${genre ? ` • ${genre}` : ''}\n\n${audioUrl}${coverArtUrl ? `\n\n${coverArtUrl}` : ''}\n\n\uD83C\uDFA7 Listen to more on BCH Radio`;

        const feedTags: string[][] = [
          ['t', 'bch'],
          ['t', 'bch-radio'],
          ['t', 'music'],
          // NIP-94 imeta so NoteContent knows this hash-based Blossom URL is
          // audio (it can't infer that from the extension-less URL alone).
          ['imeta', `url ${audioUrl}`, `m ${mime}`],
        ];
        if (coverArtUrl) {
          feedTags.push(['imeta', `url ${coverArtUrl}`, 'm image/jpeg']);
        }

        await publish({
          kind: 1,
          content: feedContent,
          tags: feedTags,
          created_at: Math.floor(Date.now() / 1000),
        });
      }

      onSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit track');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Upload className="w-4.5 h-4.5 text-primary" />
            Upload Music
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {/* Audio file upload */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Audio File *</label>
            {audioUrl ? (
              <div className="mt-1.5 flex items-center gap-2 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                <Music className="w-5 h-5 text-emerald-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 truncate">Audio uploaded</p>
                  {audioDuration > 0 && (
                    <p className="text-[10px] text-muted-foreground">Duration: {formatDuration(audioDuration)}</p>
                  )}
                </div>
                <button onClick={() => { setAudioUrl(''); setAudioMime(''); }} className="p-1 rounded hover:bg-muted">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="mt-1.5 flex flex-col items-center justify-center gap-2 w-full h-24 rounded-xl border-2 border-dashed border-border hover:border-primary/40 cursor-pointer transition-colors">
                <input
                  ref={audioFileRef}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleAudioUpload(file);
                    e.target.value = '';
                  }}
                />
                {uploading && uploadProgress.includes('audio') ? (
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Upload MP3, M4A, OGG, WAV, FLAC</span>
                  </>
                )}
              </label>
            )}
          </div>

          {/* Or paste URL */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Or paste audio URL</label>
            <input
              value={audioUrl}
              onChange={e => setAudioUrl(e.target.value)}
              placeholder="https://blossom.nostr.build/..."
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary font-mono"
            />
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Song Title *</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Song name"
              maxLength={100}
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Artist */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Artist</label>
            <input
              value={artist}
              onChange={e => setArtist(e.target.value)}
              placeholder="Artist or creator name"
              maxLength={100}
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Genre */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Genre</label>
            <select
              value={genre}
              onChange={e => setGenre(e.target.value)}
              className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">Select genre...</option>
              {GENRES.filter(g => g !== 'All').map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          {/* Cover art */}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Cover Art (optional)</label>
            {coverArtUrl ? (
              <div className="mt-1.5 relative inline-block">
                <img src={coverArtUrl} alt="" className="w-20 h-20 rounded-xl object-cover" />
                <button
                  onClick={() => setCoverArtUrl('')}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <label className="mt-1.5 flex items-center justify-center gap-2 w-20 h-20 rounded-xl border-2 border-dashed border-border hover:border-primary/40 cursor-pointer transition-colors">
                <input
                  ref={coverFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleCoverUpload(file);
                    e.target.value = '';
                  }}
                />
                {uploading && uploadProgress.includes('cover') ? (
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                ) : (
                  <Plus className="w-5 h-5 text-muted-foreground" />
                )}
              </label>
            )}
          </div>

          {/* Post to feed toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={postToFeed}
              onChange={e => setPostToFeed(e.target.checked)}
              className="rounded border-border accent-primary"
            />
            <div>
              <p className="text-xs font-medium">Post to BCH Nostr feed</p>
              <p className="text-[10px] text-muted-foreground">Announce this track to your followers</p>
            </div>
          </label>

          {/* Copyright / Rights Confirmation */}
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">Copyright Notice</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                  Only upload audio you have the rights to share. This includes original music you created,
                  Creative Commons / royalty-free tracks, or content you have explicit permission to distribute.
                  <strong className="text-foreground"> Do not upload copyrighted music.</strong>
                </p>
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={rightsConfirmed}
                onChange={e => setRightsConfirmed(e.target.checked)}
                className="mt-0.5 rounded border-border accent-primary"
              />
              <span className="text-[11px] text-foreground leading-relaxed">
                I confirm that I have the right to share this audio and it does not violate any copyright.
                I understand that tracks reported for copyright violations will be removed.
              </span>
            </label>
          </div>

          {error && (
            <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={!audioUrl || !title.trim() || submitting || uploading || !rightsConfirmed}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="w-4 h-4" /> Upload Song</>
            )}
          </button>

          <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
            Upload audio to Blossom or paste a direct URL. Supported: MP3, M4A, OGG, WAV, FLAC, OPUS.
            Find free music at <a href="https://freemusicarchive.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Free Music Archive</a>,
            {' '}<a href="https://incompetech.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Incompetech</a>, or
            {' '}<a href="https://pixabay.com/music/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Pixabay Music</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

